import { useState, useEffect, useRef } from 'react';
import { useWakeLock } from '../../hooks/useWakeLock';
import './specialty-selector.scss';
import { FaHeartbeat, FaLungs, FaBrain, FaStethoscope, FaChevronLeft, FaRandom, FaClock, FaAtom, FaBroadcastTower, FaSadCry, FaTimesCircle } from 'react-icons/fa';
import { MdExpandMore, MdExpandLess, MdChildCare, MdPregnantWoman } from 'react-icons/md';
import { GiStomach, GiKidneys, GiBrokenBone, GiTestTubes, GiTeacher, GiScalpelStrike, GiSyringe, GiNotebook } from 'react-icons/gi';
import {
  BiBody, BiFirstAid, BiBookContent,
  BiCapsule, BiHealth, BiChat,
  BiChild,
  BiAngry,
  BiCalendarCheck,
  BiTestTube,
  BiDonateBlood
} from 'react-icons/bi';
import { AiOutlineEye, AiOutlineRobot } from 'react-icons/ai';
import { TbEar, TbMoodHappy } from 'react-icons/tb';
import { useLiveAPIContext } from '../../contexts/LiveAPIContext';
import { onNavigationBack } from './navigation-events';
import { trackCaseUsage, getRemainingCases, DAILY_FREE_LIMIT } from '../../utils/caseTracker';
import { UpgradePopup } from '../upgrade-popup/UpgradePopup';
import { useAuth } from '../../contexts/AuthContext';
import PaystackPop from '@paystack/inline-js';
import { supabase } from '../../lib/supabase';
import { Shuffle, Stethoscope, Heart, Brain, Box, User } from "lucide-react";
import { SpecialtyButton } from "./SpecialtyButton";
import { SessionSummaryPopup } from '../session-summary-popup/SessionSummaryPopup';
import { FaCheckCircle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { SessionSummary } from '../session-summary/SessionSummary';
import { subscriptionService, PremiumUser } from '../../services/subscription';
import { getSessionHistory, SessionSummaryRecord } from '../../services/sessionHistoryService';
import {
  isFirstTimeUser,
  isOnboardingCompleted,
  markOnboardingComplete,
  isFirstCaseCompleted
} from '../../utils/onboarding';

// Web Audio API Sound Generator
class CallSoundEngine {
  private audioContext: AudioContext;
  private oscillators: OscillatorNode[] = [];
  private gainNode?: GainNode;
  private isRinging = false;

  constructor() {
    this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  }

  private activeTimeout?: NodeJS.Timeout;

  // Professional ringtone pattern (400ms on, 200ms off, 400ms on, 2000ms off)
  async startRingtone() {
    if (this.isRinging) return;
    this.isRinging = true;

    const createRingTone = () => {
      const osc1 = this.audioContext.createOscillator();
      const osc2 = this.audioContext.createOscillator();
      const gain = this.audioContext.createGain();

      osc1.type = 'sine';
      osc1.frequency.value = 440;  // 440Hz
      osc2.type = 'sine';
      osc2.frequency.value = 480;  // 480Hz for warble effect

      gain.gain.value = 0.15;

      osc1.connect(gain);
      osc2.connect(gain);
      gain.connect(this.audioContext.destination);

      return { osc1, osc2, gain };
    };

    const ringPattern = async () => {
      // Add check at start of each cycle
      if (!this.isRinging) return;


      const { osc1, osc2, gain } = createRingTone();
      this.oscillators.push(osc1, osc2);

      osc1.start();
      osc2.start();

      // Use timeout references for proper cleanup
      this.activeTimeout = setTimeout(() => {
        gain.gain.setValueAtTime(0, this.audioContext.currentTime);
        this.activeTimeout = setTimeout(() => {
          gain.gain.setValueAtTime(0.15, this.audioContext.currentTime);
          this.activeTimeout = setTimeout(() => {
            gain.gain.setValueAtTime(0, this.audioContext.currentTime);
            this.activeTimeout = setTimeout(() => {
              osc1.stop();
              osc2.stop();
              if (this.isRinging) ringPattern();
            }, 2000);
          }, 400);
        }, 200);
      }, 400);
    };

    await this.audioContext.resume();
    ringPattern();
  }



  stopAll() {
    this.isRinging = false;
    // Clear any pending timeouts
    if (this.activeTimeout) {
      clearTimeout(this.activeTimeout);
    }
    this.oscillators.forEach(osc => {
      osc.stop();
      osc.disconnect();
    });
    this.oscillators = [];
    this.gainNode?.disconnect();
  }
}

interface Topic {
  id: string;
  name: string;
  description: string;
  extraNotes: string;
  gender: 'male' | 'female';
}

interface Specialty {
  id: string;
  name: string;
  icon: React.ReactNode;
  topics: Topic[];
}

const SPECIALTIES: Specialty[] = [
  {
    id: 'random',
    name: 'Random Case',
    icon: <FaRandom />,
    topics: []
  },
  {
    id: 'cardiology',
    name: 'Cardiology',
    icon: <FaHeartbeat />,
    topics: [
      {
        id: 'acs',
        name: 'ACS',
        description:
          'You are an F2 in A&E John aged 49, presented to the hospital with chest pain. Please talk to the patient, take history, assess the patient and discuss your initial plan of management with the patient.',
        extraNotes: 'ACS:',
        gender: 'male'
      },
      {
        id: 'pe-mstctmy',
        name: 'PE Mstctmy',
        description:
          'You are an FY2 in A&E. Mrs. Maria Lowe, aged 45, presented with SOB and chest pain. Take history, assess her and discuss management with her.',
        extraNotes: 'Post-Mastectomy 6months + COC use + new P.E:',
        gender: 'female'
      },
      {
        id: 'pe-trnsgdr',
        name: 'PE Trnsgdr',
        description:
          'You are an FY2 in A&E. Chanella Oliver, 28 years old, has come to the hospital with chest pain. He is under transition from male to female. Talk to her and address her concerns',
        extraNotes: 'Transgender + COC use + new P.E:',
        gender: 'female'
      },
      {
        id: 'tietze',
        name: 'Tietze',
        description:
          'You are an F2 in A&E John aged 27, presented to the hospital with chest pain. Please talk to the patient, take history, discuss your initial plan of management with the patient',
        extraNotes: 'MUSCULOSKELETAL CHEST PAIN:',
        gender: 'male'
      },
      {
        id: 'shingles',
        name: 'Shingles',
        description:
          'You are F2 in GP Peter aged has presented to the clinic with chest pain. Talk to the patient, assess her and discuss the plan of management.',
        extraNotes: 'CHEST PAIN-HERPES ZOSTER:',
        gender: 'male'
      },
      {
        id: 'pericard',
        name: 'Pericard',
        description:
          'You are an F2 in A&E John aged 30, presented to the hospital with chest pain. Please talk to the patient, take history, assess the patient and discuss your initial plan of management with the patient.',
        extraNotes: 'CHEST PAIN-PERICARDITIS:',
        gender: 'male'
      },
      {
        id: 'pe',
        name: 'PE',
        description:
          'You are an FY2 in GP practice. Angela 34-year-old has presented to you with complain of breathlessness. Talk to the patient & address her concerns. At 6 minutes discuss D/D with the patient.',
        extraNotes: 'PULMONARY EMBOLISM:',
        gender: 'female'
      },
      {
        id: 'hf-2-mi',
        name: "HF 2' MI",
        description:
          'You are FY2 in medicine. Mr. David Parker aged, 59 presented to the hospital with breathlessness. Patient has been referred by the GP. Patient had an MI 7years ago. Patient is not regular with the GP. Please take history, assess his condition, discuss about management and address his concerns.',
        extraNotes: 'HEART FAILURE (SOB POST MI):',
        gender: 'male'
      },
      {
        id: 'arrythmia',
        name: 'Arrythmia',
        description:
          'You are FY2 in GP clinic. Mr. Alexander aged, 57presented to the hospital complaining of chest discomfort. This is patient’s first visit. Please talk to the patient, take a focused history, and discuss your initial plan of management with the patient. Do not examine the patient.',
        extraNotes: 'ARRHYTHMIA:',
        gender: 'male'
      },
      {
        id: 'pad',
        name: 'PAD',
        description:
          'Mr. James Anderson 55 years presented in GP surgery have some concerns. Talk to him assess and manage him accordingly.',
        extraNotes: 'PERIPHERAL ARTERIAL DISEASE:',
        gender: 'male'
      },
      {
        id: 'post-angio',
        name: 'Post-Angio',
        description:
          'You are F2 in cardio. Anne Boleyn 65 years old lady was admitted in the hospital with MI 2 days ago. She had angiography and angioplasty for that. Physiotherapist advised her to walk but she is afraid. She has asked the nurse if she could talk to you. Please talk to her and address her concern.',
        extraNotes: 'POST ANGIOPLASTY-HESITANT TO WALK:',
        gender: 'female'
      }
    ]
  },

  {
    id: 'neuro',
    name: 'Neuro',
    icon: <FaBrain />,
    topics: [
      {
        id: 'sah',
        name: 'SAH',
        description:
          'You are FY2 in A&E. Michelle aged, 45 presented to the hospital with headache. Talk to the patient; take history, do relevant examination and plan of management with the patient. After 6 minutes give your management plan to the examiner',
        extraNotes: 'SUBARACHNOID HAEMORRHAGE:',
        gender: 'female'
      },
      {
        id: 'gca',
        name: 'GCA',
        description:
          'You are F2 in general medicine. Maria aged, 55 came to the hospital with headache. Take a focus history, assess the patient, do examination and discuss further management with the patient.',
        extraNotes: 'HEADACHE, GCA:',
        gender: 'female'
      },
      {
        id: 'tension',
        name: 'Tension',
        description:
          'You are FY2 in GP. Michelle aged, 45 presented with headache. Talk to the patient; take history, assess her and discuss the plan of management with the patient',
        extraNotes: 'TENSION HEADACHE:',
        gender: 'female'
      },
      {
        id: 'hangover',
        name: 'Hangover',
        description:
          'You are FY2 in University Clinic. Mr. Jack Daniel aged 22 presented with headache. Talk to the patient, take history, assess her and discuss the plan of management with the patient',
        extraNotes: 'HANGOVER HEADACHE:',
        gender: 'male'
      },
      {
        id: 'tia-counsel',
        name: 'TIA Counsel',
        description:
          'You are F2 in A&E. Mrs. Olivia Jones aged, 64 has been brought to the hospital by her husband due to weakness on one side of body, slurred speech, dropping of the angle of her mouth and difficulty in swallowing. Patient symptoms improved after 2 hours. Patient is completely fine now. Patient cannot remember what happen 2 hours ago. BP was measured and recorded as 150/100. General and neurological examination has been done and there is no finding. Routine blood test has been done and result is awaited. Please talk to her husband, take history and discuss about your further management with him. Consent has been taken from wife to talk to the husband.',
        extraNotes: 'TRANSIENT ISCHAEMIC ATTACK:',
        gender: 'male'
      },
      {
        id: 'migraine',
        name: 'Migraine',
        description:
          'FY2 in A&E, 30-year-old lady with headache, second time coming to A&E, came first time 1 week ago. Other info: Pain 8/10. Took ibuprofen + PCM, but it did not help. Take a focused history and discuss management with patient.',
        extraNotes: 'MIGRAINE: Patient Info: - One side of the head (left)n - Sudden onset last nightn - Dull and static pain, severity 8/10n - Nothing makes it well or bettern - Triggers; - Menses - Stress - Feels nauseous + blurred vision - Sees horizontal line - Had headache several times in the past, can\u2019t remember first time, last time was 1 week ago - Last time, nothing was done, no investigation, no medications, discharged after reassurance - Works in a library. Candidate\'s Question:',
        gender: 'female'
      },
      {
        id: 'trig-neur',
        name: 'Trig Neur',
        description:
          'You are FY2 in GP. Mr. Damian Oldfield, aged 60, has come to you with a facial pain. Please talk to him and address his concerns.',
        extraNotes: 'TRIGEMINAL NEURALGIA:',
        gender: 'male'
      },
      {
        id: 'gbs',
        name: 'GBS',
        description:
          'You are an F2 in GP Clinic. Andrea Downing aged 33 presents with difficulty in walking since last few days. Please talk to the patient, take focussed history, and discuss further management.',
        extraNotes: 'GUILLAIN-BARRE SYNDROME:',
        gender: 'female'
      },
      {
        id: 'ms-opt-n',
        name: 'MS + Opt N',
        description:
          'You are an F2 in GP Clinic, Mrs. Amelia White, 28, came to the clinic because of a problem with her vision. Talk to her and explain to her the treatment options.',
        extraNotes: 'MULTIPLE SCLEROSIS: Vision problems, eye mvmt pain, tingling and numbness of hands, mother with MS:',
        gender: 'female'
      },
      {
        id: 'encephal',
        name: 'Encephal',
        description:
          'You are F2 in A&E. Mr. Peter aged, 22 has been brought to the hospital by his father after having a fit. They were watching a football match with his father and suddenly he started having a fit. After the fit he started hallucinating, having odd behavior and saying strange things. He is now confused, drowsy and agitated. He is not unconscious. Patient is having fever and headache for the past 2 days. Please talk to the father, explain the provisional diagnosis to the father and explain about his son’s condition, discuss your management plan with him and address his concerns. You will find the clinical findings and investigation reports inside the cubicle.',
        extraNotes: 'ENCEPHALITIS:',
        gender: 'male'
      },
      {
        id: 'head-inj',
        name: 'Head Inj',
        description:
          'You are F2 in A&E. Mr. Peter Smith aged, 40 was brought to the hospital by the ambulance due to having a fall $$at a restaurant$$. Please talk to the patient, take history, assess your patient and discuss your initial plan of management with the patient.',
        extraNotes: 'HEAD INJURY (ADULT) + Transient LOC + Normal neuro examination + May need Brain CT based on criteria:',
        gender: 'male'
      },
      {
        id: 'ph-neura',
        name: 'PH Neura',
        description:
          'You are FY2 in GP. Benjamin White, aged 72, has come for consultation. He was diagnosed with Shingles 2 months back and was given Acyclovir. He saw his GP 1 month back for the pain on the right side of his chest and was given Paracetamol and Codeine. Talk to him and address his concerns.',
        extraNotes: 'POST HERPETIC NEURALGIA on followup. Still has pain:',
        gender: 'male'
      },
      {
        id: 'crbellr-atx',
        name: 'Crbellr Atx',
        description:
          'You are F2 in General medical ward. Janet Clarke aged, 65 has been referred by her GP because of suspicion of Cerebellar ataxia. Patient is diagnosed of Diabetes. Her diabetes is well controlled. Take history, do relevant examination and discuss a management plan with your patient.',
        extraNotes: 'CEREBELLAR ATAXIA, Feels clumsy, cannot knit, candidate to demonstrate CEREBELLAR EXAMINATION:',
        gender: 'female'
      },
      {
        id: 'citalopram',
        name: 'Citalopram',
        description:
          'You are F2 in the GP clinic. Avery aged 50 has come to the clinic with complaint of tiredness. She was diagnosed with depression and she is taking Citalopram 10mg. Her eGFR is >60ml/min. Take history, assess her & discuss the management plan with the patient.',
        extraNotes: 'CITALOPRAM SIDE EFFECT = TIREDNESS. Low sodium on tests if candidate asks:',
        gender: 'female'
      }
    ]
  },


  {
    id: 'chest',
    name: 'Chest',
    icon: <FaLungs />,
    topics: [
      {
        id: 'chr-cough-dd',
        name: 'Chr Cough DD',
        description:
          'You are F2 in respiratory department. Peter aged, 70 has come to you with cough for past few months. Please talk to the patient, take history, and assess the patient’s condition. After 6minutes, discuss about differential diagnosis with examiner and discuss about the management plan with patient.',
        extraNotes: 'RESPIRATORY PROBLEMS: DRY COUGH for months (DD: TB, Lung cancer, Mesothelioma, COPD, Asthma, Pneumonia, Heart failure):',
        gender: 'male'
      },
      {
        id: 'pcp',
        name: 'PCP',
        description:
          'You are F2 in medicine. Michael aged 24 presented to the hospital with cough and shortness of breath for the past few weeks. Patient is homeless and he is losing weight. Please talk to the patient, assess the patient, do relevant examination and do initial management with patient.',
        extraNotes: 'DRY COUGH PCP:',
        gender: 'male'
      },
      {
        id: 'lung-ca',
        name: 'Lung Ca.',
        description:
          'You are FY2 in Medicine. 60-year-old man presents with complaints of cough since few months. He has coughed up blood few times in the last week. Take history, examine and discuss management with the patient.',
        extraNotes: 'COUGH \u2013 LUNG CANCER:',
        gender: 'male'
      },
      {
        id: 'pneumonia',
        name: 'Pneumonia',
        description:
          'You are F2 in A & E. Daniel aged, 72 presented with cough and shortness of breath. He has been referred by his GP. Nurse colleague has seen the patient. The vitals have been recorded in observation chart. Chest X-Ray has been taken. You can find the NEWS chart and CXR inside of the cubicle. Talk to the patient, take relevant history, assess the patient, and discuss about initial management plan with the patient.',
        extraNotes: 'COUGH & SOB (PNEUMONIA):',
        gender: 'male'
      },
      {
        id: 'tb',
        name: 'TB',
        description:
          'You are F2 in A&E. Thomas aged, 29, presented with cough and SOB. Talk to the patient, take relevant history, assess the patient and outline the plan of management with him.',
        extraNotes: 'COUGH & SOB (TB):',
        gender: 'male'
      },
      {
        id: 'asthma',
        name: 'Asthma',
        description:
          'You are F2 in GP. Adam aged, 22 presented to the hospital with wheeze and chest tightness. Take a focused history, assess the patient, discuss diagnosis and about management plan.',
        extraNotes: 'Simman - Acute Exercebation of Asthma (Severe).',
        gender: 'male'
      },
      {
        id: 'asth-disch',
        name: 'Asth Disch.',
        description:
          'You are F2 in General Medicine. Mr. Peter Lewis, aged 22, presented to the hospital due to severe asthmatic attack three days ago. Patient was diagnosed with asthma four weeks ago and this is his first asthma attack. He has recovered quickly and is stable now. He is getting discharged today and was prescribed with three medications. Salbutamol PRN, Beclomethasone 400 microgram BD, Prednisolone 30mg OD 3 days. Please talk to the patient, assess his fitness for discharge, explain the medications and address his concerns.',
        extraNotes: 'ASTHMA DISCHARGE:',
        gender: 'male'
      },
      {
        id: 'spacer-use',
        name: 'Spacer Use',
        description:
          'You are F2 in Paediatrics. Rhodes aged, 4 has been admitted to the hospital with breathlessness. His mother Ann is worried that her son feels well when in the hospital but deteriorates when at home. She wants to talk to you about her son. Please talk to her and address her concern.',
        extraNotes: 'WRONG SPACER DEVICE USE:',
        gender: 'male'
      },
      {
        id: 'osas',
        name: 'OSAS',
        description:
          'You are an FY2 in GP clinic. Mr. Sloan aged 46 has come with complain of tiredness. Talk to him, take history & discuss the management plan with the patient.',
        extraNotes: 'OBSTRUCTIVE SLEEP APNOEA:',
        gender: 'male'
      },
      {
        id: 'hemoptysis',
        name: 'Hemoptysis',
        description:
          'You are F2 in in respiratory department. Jacob aged, 55 has come to the clinic because of cough and hemoptysis. Please talk to the patient, take relevant history, assess the patient, discuss about initial management plan with patient and address his concern. X-ray has been done and you can find it in the cubicle',
        extraNotes: 'HAEMOPTYSIS:',
        gender: 'male'
      },
      {
        id: 'sepsis',
        name: 'Sepsis',
        description:
          "You are F2 in AMU. Mrs. Olive Green, aged 85, has been referred to the hospital from a care home. The patient is confused and agitated, and you are not able to talk to her to take any history. There is no medical record or reference letter from the care home, and you are unable to examine her. Vitals have been recorded as follows: BP: 90/60 mmHg, Pulse: 120/min, RR: 21/min, Temp: 38.5°C, O2 Sat: 88%. Call the care home, talk to a member of the care home staff, and take a history about the patient. After 6 minutes, talk to the examiner regarding the provisional diagnosis and discuss the management in the best interest of the patient.",
        extraNotes: 'SIMMAN - Post UTI Septic Shock. Initial Monitor: RR 24 SpO2 88 Pulse 96 BP 105/65 Temp 39.0',
        gender: 'female'
      }
    ]
  },


  {
    id: 'rheuma',
    name: 'Rheuma',
    icon: <GiBrokenBone />,
    topics: [
      {
        id: 'cfs',
        name: 'CFS',
        description:
          'You are F2 in GP. Mr. Steve Barone, aged 45, presented with tiredness. He has been to the hospital 6 months ago with the same problem after having the flu. The IT system in the clinic has crashed, and there are no medical records available for the patient. Talk to the patient, take history, and discuss the management plan. Address the patient\'s concerns. The patient presented to the GP six months ago for the same issue, and no medication was prescribed at that time.',
        extraNotes: 'Chronic Fatigue Syndrome.',
        gender: 'female'
      },
      {
        id: 'pmr',
        name: 'PMR',
        description:
          'You are FY2 in Rheumatology Clinic. Mrs. Jackson has presented with some concerns. Talk to her, take history, and discuss the management plan. Address the patient\'s concerns.',

        extraNotes: 'PMR - Pain & Aches.',
        gender: 'female'
      },
      {
        id: 'pmr-flwup',
        name: 'PMR FlwUp',
        description:
          'You are an FY2 in GP. Mrs. Janet James, aged 50, has been diagnosed with polymyalgia rheumatica. She is on steroids, aspirin, lansoprazole and bisphosphonates. ESR and CRP are normal.Talk to her and address her concerns.',
        extraNotes: 'POLYMYALGIA RHEUMATICA (PMR) REFUSING STEROIDS (due to Side effects of steroids). \'I don\'t want to take steroids anymore\'. have been on steroids for 1 year. gained 2 stones weight. developed hypertension and diabetes. pains has stopped. on bisphosphonates. vital signs: Weight: 13 stones (82.5 kg). Height: 171 cm.',
        gender: 'female'
      },
      {
        id: 'strd-rvw',
        name: 'Strd Rvw',
        description:
          'Jackson Avery, 40 years old, has called you to talk about certain concerns. He is a known case of polymyalgia rheumatica and is currently on lansoprazole, bisphosphonates, and steroids. Talk to him and address his concerns.',
        extraNotes: 'Steroid Review. Polymyalgia Rheumatica. Patient is enquiring about steroid side effects. First sentence \'Doctor, I have certain concerns about the medications that I am taking\'. Much later if doctor probes say: \'I want to know the side effects of steroid.\' concerns after doctor has told you the side effects: \'Doctor why should I take this medication since there are so many side effects?\'',
        gender: 'male'
      },
      {
        id: 'post-hypot',
        name: 'Post Hypot',
        description:
          'You are F2 in A and E. Mrs. Marley, aged 72, has been brought to the hospital with a fall. Please talk to the patient, take history, do relevant examination, and discuss the management with the patient. The patient doesn’t have her medications on her. You will find extra information in the cubicle.',
        extraNotes: 'Postural Hypotension. Known Hypertensive. recently changed GP with unknown medication. lives with husband. first sentence: \'I am falling frequently these days. It is happening from last few days. When doctor asks for more info reply: \'Dr. I like going out with my friends. But now I am scared to go out with my friends. Whenever I am standing I feel dizzy and then I fall. This time I was shopping in the mall, I felt dizzy and fell down and then an ambulance brought me here.\' Vital signs: EX: BP standing- 110/70. BP lying- 150/90, PR- 80. T- 37. RR- 12-20. 02 Sat- 96. ECG - normal.',
        gender: 'female'
      },
      {
        id: 'fall-hip',
        name: 'Fall + #',
        description:
          'You are F2 in Surgery. Mrs. Sharon, aged 78, has recently had a fall and fractured the neck of her femur. She has undergone surgery. Your consultant has asked you to take history and find out the possible cause behind the fall. Please talk to the patient, take history, find out the cause of the fall, and discuss the management plan with the patient.',
        extraNotes: 'Fall & Hip Fracture. fall 4 days ago. surgery 2 days ago. \'Doctor, my husband and I love cooking. We were in the kitchen making lunch together. I was standing and talking to my husband and I suddenly fell.\' can\'t remember during the fall. after fall ambulance brought her to hospital with right hip fracture. known HTN and arthritis on meds. Candidate is not expected to know the specific cause of the fall but rather to explore differential diagnoses: Postural hypotension, Anemia, Confusion (UTI, Pneumonia, Constipation), Cardiac arrhythmias, DM, HTN, Vision Problems.',
        gender: 'female'
      }
    ]
  },

  {
    id: 'gastro',
    name: 'Gastroenterology',
    icon: <GiStomach />, // Ensure you import the appropriate icon from react-icons
    topics: [
      {
        id: 'gerd',
        name: 'GERD',
        description: 'You’re an F2 in GP. A 55-year-old patient has come with complaints of indigestion. Please talk to the patient, assess him, discuss management, and address his concerns.',
        extraNotes: 'Indigestion. burning sensation on the chest (Points towards epigastric region). water brash. used antacids but unhelpful.',
        gender: 'male'
      },
      {
        id: 'dysphagia',
        name: 'Dysphagia',
        description: 'You are F2 Surgery. Mr. Adam, aged 60, presented to the hospital complaining of difficulty in swallowing. Please talk to the patient, assess him, and discuss your initial management plan with him.',
        extraNotes: 'Dysphagia. weight loss. anorexia. concern: "is it cancer?" Diagnosis: possibly Oesoph Ca. Candidate must do urgent referral to pass.',
        gender: 'male'
      },
      {
        id: 'hematemesis',
        name: 'Hematemesis',
        description: 'You are F2 in A&E. Maria, aged 27, presented to the hospital with haematemesis. The vitals have been measured as follows: BP: 110/70 mmHg, PR: 100/min, T: 37°C, RR: 17/min, O2 Sat: 97%. Please talk to the patient, take history, assess the patient, and discuss further management with her.',
        extraNotes: 'Haematemesis. Takes ibuprofen due to hangover headache for 6months.',
        gender: 'female'
      },
      {
        id: 'enteritis',
        name: 'Acute Gastroenteritis',
        description: 'You are an F2 in A&E. Mrs. Alice Parker, aged 40, has presented with complaints of vomiting and diarrhea for the last 3 days. Assess the patient and provide relevant management. You are aware that food poisoning is a notifiable disease.',
        extraNotes: 'Acute Gastroenteritis or Food Poisoning. No fever. Had dinner at a restaurant 3days ago. Same issue husband and child. Patient initially object to notification because its her friend\'s restaurant...but later agree.',
        gender: 'female'
      },
      {
        id: 'barretts',
        name: 'Barrett\'s Oesophagitis',
        description: 'You are F2 in Surgery. Louis, aged 40, had an endoscopy and biopsy, and he is here for his reports. The diagnosis of Barrett’s oesophagus has been made. It is columnar metaplasia, which is pre-malignant, non-invasive, and non-metastatic. He was advised to repeat the endoscopy after 3 years. Talk to the patient, take history, explain the diagnosis, discuss the management, and address his concerns. You can find the reports in the cubicle.',
        extraNotes: 'Barrett\'s Oesophagitis. c/o heartburn 6months',
        gender: 'male'
      },
      {
        id: 'ibs',
        name: 'Irritable Bowel Syndrome',
        description: 'You are an F2 working in GP. David Lloyd, aged 50, has come in with some abdominal discomfort. Talk to the patient, assess him, and discuss the management plan with him.',
        extraNotes: 'Irritable Bowel Syndrome (IBS). abdominal discomfort. bloating. diarrhoea and constipation. takes 5cups of coffee. stressed at work - Banker.',
        gender: 'male'
      },
      {
        id: 'gilberts',
        name: 'Gilbert\'s Syndrome',
        description: 'You are an F2 in GP. Adam, aged 25, has come to the clinic to discuss his blood test results. Please talk to the patient, review the results, and address his concerns. Results: AST 20 U/L (reference range: 5-40 U/L), ALT 30 U/L (reference range: 5-40 U/L), ALP - Normal (30-130 umol/L), GGT - Normal, Albumin - Normal (38-50 g/L), Bilirubin 39 umol/L (reference range: <21 umol/L), Direct bilirubin - Normal, Indirect bilirubin - Elevated.',
        extraNotes: 'Abnormal LFTs-Gilbert\'s Syndrome. Everything is normal.',
        gender: 'male'
      },
      {
        id: 'alc-foot',
        name: 'Alcoholic Foot',
        description: 'You are F2 in Medicine. Juliet, aged 50, presented to the hospital with a burning sensation in both feet. She attended an alcohol cessation clinic 6 months ago. Take a focused history, perform a relevant examination, and discuss the management with the patient.',
        extraNotes: 'Alcoholic Foot. Examination: loss of sensation below knee.',
        gender: 'female'
      },
      {
        id: 'alc-hepat',
        name: 'Alcoholic Hepatitis',
        description: 'You are FY2 in a GP clinic. A 40-year-old man came for his blood reports. Results: Bilirubin - Normal, ALT - Normal, AST - 63 (Raised). Explain the results to him and discuss further management.',
        extraNotes: 'Alcoholic Hepatitis. Patient initially came for routine checkup. Now for results. no symptoms. Takes half glass of wine daily x 20yrs. when asked to stop alcohol he says, "I love alcohol, I can\'t stop."',
        gender: 'male'
      },
      {
        id: 'alcoholism',
        name: 'Alcoholism',
        description: 'You are the junior doctor in the GP clinic. Mrs. Amanda More has undergone tests a few days ago and has called to ask about her results. Results: Hb 11 (normal range: 11-13), ALT raised, MCV raised. Address her concerns and discuss the appropriate management plan.',
        extraNotes: 'NHS Health Check (Raised ALT, Macrocytosis). ?Alcoholism. Its a telephone conversation. Routine checkup. No symptoms. excess alcohol intake. if doctor asks you to come in for examination, ask "why? is everything ok?" then agree if they try to convince you.',
        gender: 'female'
      },
      {
        id: 'arld',
        name: 'Alcohol-Related Liver Disease',
        description: 'You are F2 in GP. Olivia, aged 40, came to the hospital because of abdominal distension. Talk to the patient, assess her, perform a relevant examination, and discuss the initial management plan with the examiner.',
        extraNotes: 'Alcohol-Related Liver Disease (ARLD). Abdomen distension 2-3months. Alcohol excess 15yrs.',
        gender: 'female'
      },
      {
        id: 'bowel-ca',
        name: 'Bowel Cancer',
        description: 'You are an F2 in general medicine. Mr. David Peterson, aged 40, presented to the hospital complaining of diarrhoea. The patient has been referred by his GP. Please talk to the patient, take a focused history, assess the patient, perform relevant examinations, and discuss your initial management plan with the patient.',
        extraNotes: 'Chronic Diarrhoea- Possible Bowel Cancer. Diarrhoea + Abdominal discomfort/dull pain at LIF. weight loss 2-3stones. CONCERNS: "doctor is it cancer?" "what will you do for me?" "how long will I wait before referral".',
        gender: 'male'
      }
    ]
  },

  {
    id: 'renal',
    name: 'Renal',
    icon: <GiKidneys />,
    topics: [
      {
        id: 'nephrpth',
        name: 'Nephrpth',
        description:
          'You are an F2 in GP. James Anderson, aged 30, came to the clinic with a new problem. His eGFR is low, and his creatinine is high. Please talk to the patient, discuss the management plan, and address his concerns.',
        extraNotes: 'Analgesic Nephropathy.',
        gender: 'male'
      },
      {
        id: 'up-urea-down-na',
        name: '↑Urea,↓Na+',
        description:
          'You are F2 in A&E. An 82-year-old patient has been admitted to the hospital with a new problem and is now confused. Talk to his daughter, discuss the management plan with her, and address her concerns. There are some blood tests inside the cubicle. Results: Hb-122*, WBC-Normal, Na-115, Urea-raised, Creatinine-raised.',
        extraNotes: 'Uraemia & Hyponatraemia. Dr: How can 1 help you? Patient: I am worried about my father as he is confused. Dr: Could you please tell me more about his condition? Patient: Actually his problem started 2 weeks back but now it is getting worse and now he is not even recognizing me. Past Med Hx: HTN, Stroke. fully recovered after stroke. Was admitted recently and given water tablets.',
        gender: 'male'
      },
      {
        id: 'uti-sepsis',
        name: 'UTI Sepsis',
        description:
          'You are F2 in A&E. Henry, aged 75, was brought to the hospital by his wife due to confusion. He was diagnosed with a UTI 3 days ago. Please talk to them and address their concerns.',
        extraNotes: 'Septic Shock 2\' UTI (Confusion for 1day, fever 3days, peeing often/burning sensation for 3days). D: What brought you to the hospital? Patient: \'Where am I?\' \'what am I doing here?\' Patient\'s wife: I don\'t know why is he behaving like this? D: Could you please tell me what exactly is the problem? Patient\'s wife: He is acting strangely Dr. He is not talking to me. He is confused. He is asking questions like \'Where am I?\' \'what am I doing here?\' Vital signs: BP-80/50, PR-110. T- 38C, Sats-90%. The doctor must do SEPSIS SIX to pass this station.',
        gender: 'male'
      },
      {
        id: 'uti-bph',
        name: 'UTI + BPH',
        description:
          'You are F2 in GP. Mr. Arthur, aged 75, presented to the hospital with dysuria. Please talk to the patient, take history, do a relevant examination, and discuss your initial management plan with the patient.',
        extraNotes: 'UTI & BPH. burning while peeing, lower tummy pain, smelly cloudy urine, fever 3days, +ve lower urinary tract symptoms. No weight loss. allergy to penicillin. EX: BP- 110/70, HR- 70. T- 38C, 02 sat- 96. Abdomen: Tenderness over suprapubic area. DRE: Both the lobes of prostate are enlarged and prostate is smooth. Urine dipstick- Nitrates, Leucocytes and Micro-hematuria. Patient\'s concerns: \'Doctor, is it a cancer?\' Dr: May I know why are you concerned about cancer? P: Doctor, because my friend had prostate cancer.',
        gender: 'male'
      }
    ]
  },
  {
    id: 'endocrinology',
    name: 'Endocrinology',
    icon: <FaAtom />,
    topics: [
      {
        id: 'hypothyr',
        name: 'Hypothyroidism',
        description: 'You are F2 Medicine. Mrs. Matilda, aged 60, presented to the hospital with feelings of tiredness. Please take a focused history, assess the patient, provide your possible diagnosis to the patient, and discuss your initial management plan with her.',
        extraNotes: 'Hypothyroidism. Tiredness 2yrs. Weight gain. Constipation.',
        gender: 'female'
      },
      {
        id: 'hyperthyr',
        name: 'Hyperthyroidism',
        description: 'You are the F2 in GP. Sue Smith, aged 22, presents to the clinic with a problem. Please talk to the patient, take a relevant history, assess the patient, and discuss the management plan with her.',
        extraNotes: 'Hyperthyroidism. Boyfriend told patient she is losing weight. feels hotter. lighter menstruation. frequent bowel movements. family hx in sister. eats more often. EX: - Vitals: BP-120/80, PR- 120, T-37. RR-12-20, 02 sat-96. TSH- 0.3 (0.5-4.5 mU/1). T3- 6.2 (3.5-7.8 microgram/dl). T4- 35 (4.6-12microgram/dl).',
        gender: 'female'
      },
      {
        id: 'thyr-revw',
        name: 'Thyrotoxicosis Review',
        description: 'You are F2 in the GP. Lucy Talbot, aged 40, came for her annual review. She was diagnosed with thyrotoxicosis and has been taking carbimazole for the past year. Talk to her and discuss the management plan.',
        extraNotes: 'Thyrotoxicosis. Annual Review. Candidate is expected to do thyroid and related examination in order to pass this station.',
        gender: 'female'
      },
      {
        id: 'thyr-result',
        name: 'Thyroid Blood Results',
        description: 'You are an FY2 in GP. Dorothy Perkins, aged 81, has come to you for a medication review. She has been on Amlodipine 10 mg for 2 months. Other medications include atorvastatin 20 mg and levothyroxine 125 mcg. Blood results: TSH <0.02, T4 24, Lipid profile, U&Es, LFTs – Normal. Talk to her and review the medications and blood results.',
        extraNotes: 'Hyperthyroidism. Discuss Blood Results.',
        gender: 'female'
      },
      {
        id: 'hyperparathy',
        name: 'Hyperparathyroidism',
        description: 'You are an FY2 in GP. A 55-year-old patient has come in for her results. CBC, urea, electrolytes, HbA1c, and ESR are normal. Calcium (corrected): 3.05 mmol/L (reference range: 2.2 to 2.6 mmol/L), Parathyroid Hormone: increased (10-65 ng/L). Talk to her and address her concerns.',
        extraNotes: 'Hyperparathyroidism. c/o tiredness, constipation, increased thirst for months.',
        gender: 'female'
      }
    ]
  },
  {
    id: 'haemat',
    name: 'Haematology',
    icon: <BiDonateBlood />, // Ensure you import the appropriate icon from react-icons
    topics: [
      {
        id: 'vitb12def',
        name: 'Vitamin B12 Deficiency',
        description: 'You are F2 in GP. Sharon, aged 40, came to the hospital with tiredness. She previously visited the GP, had low hemoglobin, and was given folic acid. She has returned with the same symptoms, and repeat blood tests showed low Vitamin B12 levels. Please talk to her and address her concerns.',
        extraNotes: 'Vitamin B12 Deficiency. tiredness 1 yr. vegan diet for last 2 years.',
        gender: 'female'
      },
      {
        id: 'thalasemia',
        name: 'Thalassemia',
        description: 'You are FY2 in the GP clinic. Mrs. Mary, aged 45, has come for a well-woman checkup. Her blood test results from three weeks ago are as follows: Hb: 110 g/dl (115-165), TLC: 4000/cmm, PLT: 430,000, MCV: 58 (80-100), U&E: Normal, LFTs: Normal, Serum Iron: Normal, Serum Ferritin: Normal. Discuss these test results with Mrs. Mary, take an appropriate history, and discuss the management with her.',
        extraNotes: 'Anaemia 2 Thalasemia. Nil complaints/symptoms. for checkup then blood tests were done. now came for test results followup. family hx of thalasemia in sister.',
        gender: 'female'
      },
      {
        id: 'myeloma',
        name: 'Multiple Myeloma',
        description: 'You are an FY2 in GP. Mrs. Walker, aged 52, has come in for her blood results. Results: Hb: 100, MCV: Normal, RA factor: Normal, IgG: Raised, Urine: Bence Jones Protein Positive. Talk to her and address her concerns.',
        extraNotes: 'Multiple Myeloma. came for test results. have backache (lower back) & fatigue. PATIENT\'S CONCERNS: \'1. Is it rheumatoid arthritis?\' \'2. Is it cancer?\' \'3. What are you going to do for me?\'',
        gender: 'female'
      },
      {
        id: 'leukemia',
        name: 'Leukemia',
        description: 'You are an F2 in GP. Mr. John Bernard, aged 55, came to the clinic with gum bleeding during his Wellman check-up. Please talk to the patient, discuss the management plan, and address his concerns.',
        extraNotes: 'Leukaemia. Other symptoms negative. Give candidate the examination findings once they mention abdominal examination: Abdomen: Splenomegaly. once they mention blood counts give this: WBC count: >100,000. The doctor must refer urgently (cancer suspect pathway) within 2 weeks to pass this station. Concerns: how long will I wait for the test.',
        gender: 'male'
      },
      {
        id: 'itp',
        name: 'Immune Thrombocytopenic Purpura (ITP)',
        description: 'You are an F2 in GP. Mark Anthony, aged 45, has been feeling tired for the last 1-2 weeks and came to the clinic. Please talk to the patient, discuss the management plan, and address his concerns.',
        extraNotes: 'Immune Thrombocytopenic Purpura (ITP). Tiredness getting worse throughout the day. cough +fever 3wks ago. lots of bruises. Give candidate the examination findings once they mention abdominal examination and blood counts: Abdomen: Splenomegaly; Bloods: Thrombocytopenia',
        gender: 'male'
      },
      {
        id: 'ida-clnscpy',
        name: 'Iron Deficiency Anaemia (Colonoscopy)',
        description: 'You are an FY2 in A&E. John, aged 55, has come to the hospital with per rectal bleeding. Blood reports show Hb: 100 (low), with normal LFT, KFT, and TLC. The consultant has decided to perform a colonoscopy. Talk to him, explain the lab reports, and address his concerns.',
        extraNotes: 'Iron Deficiency Anaemia. Came for laboratory results. To be Counselled for colonoscopy. Patient refuses initially but finally agrees when the candidate convinces him. Reasons for refusal: 1. "I have already so many tests and I don\'t want to do any more." 2. "I heard its painful" 3. "I am scared of cancer. my friend died of cancer." Bring up the concerns one after the other.',
        gender: 'male'
      }
    ]
  },
  {
    id: 'surgery',
    name: 'Surgery',
    icon: <GiScalpelStrike />,
    topics: [
      {
        id: 'back-pain-prostate-cancer',
        name: 'Back Pain - Prostate Cancer',
        description: 'You are F2 in GP. Peter Smith, aged 58 years, presents to the clinic complaining of back pain. Talk to the patient, take history, assess the patient, and discuss further management with the patient while addressing his concerns.',
        extraNotes: '$$Back Pain 3months. Prostate Cancer. +ve LUTS. but no perineal anaesthesia or anal incontinence. reduced appetite. lost weight.$$',
        gender: 'male'
      },
      {
        id: 'ivdp-cauda-equina',
        name: 'IVDP - Cauda Equina Syndrome',
        description: 'You are an F2 in A&E. Jermaine, aged 40, has presented to the hospital complaining of back pain. Talk to him, assess him, and discuss the management plan with the patient.',
        extraNotes: '$$Back Pain (IVDP). +/- Cauda Equina syndrome.$$',
        gender: 'male'
      },
      {
        id: 'back-sprain',
        name: 'Back Sprain',
        description: 'You are an F2 in A&E. John, aged 30, came to you with acute back pain. Talk to the patient, assess him, and provide the further plan of management.',
        extraNotes: '$$Back Sprain. Started while playing squash. Patient Concerns: \'Will you give me physiotherapy?\' \'When can I resume my exercise?\' \'Can I play squash?\'$$',
        gender: 'male'
      },
      {
        id: 'abdominal-aortic-aneurysm',
        name: 'Abdominal Aortic Aneurysm',
        description: 'You are FY2 in A&E. Mr. Robert, aged 60, has presented to you with a complaint of back pain for the last 1 day. The patient has been diagnosed with HTN for the past 10 years. Talk to the patient, take history, assess the patient, and discuss the plan of management.',
        extraNotes: '$$Abdominal Aortic Aneurysm.$$',
        gender: 'male'
      },
      {
        id: 'acute-cholecystitis',
        name: 'Acute Cholecystitis',
        description: 'You are an F2 in A&E. John Smith, aged 57, came to the hospital with abdominal pain. Please talk to him, assess him, discuss your plan of management, and address his concerns.',
        extraNotes: '$$Acute Cholecystitis. Examination findings: T=38.5C, Abdomen tender at hypochondrium.$$',
        gender: 'male'
      },
      {
        id: 'diverticulitis',
        name: 'Diverticulitis',
        description: 'You are FY2 in A&E. Aleena, aged 42, came with lower abdominal pain. The nurse has taken the vitals and sent the patient to you. Talk to the patient, do an examination, and discuss the management plan with the patient.',
        extraNotes: '$$Diverticulitis. Examination findings: T-38-39C PR- 110/min BP- 130/80 RR- 12-20 O2 sat \u2013 96%, Abdomen - marked tenderness at LIF.$$',
        gender: 'male'
      },
      {
        id: 'intestinal-obstruction',
        name: 'Intestinal Obstruction',
        description: 'You are FY2 in the emergency department. Mr. Peter Smith presented with abdominal pain for the past 2 days. Talk to him, take history, and manage him accordingly.',
        extraNotes: '$$Intestinal Obstruction. Examination findings: Distended abdomen, tympanic sound on the percussion of the abdomen due to an air-filled stomach and high-pitched bowel sounds$$',
        gender: 'male'
      },
      {
        id: 'neck-lump-cancer',
        name: 'Neck Lump - Cancer',
        description: 'You are FY2 in GP. Mr. William Harding, aged 35, has come to you with a neck swelling. Please talk to him, assess him, and address his concerns.',
        extraNotes: '$$Neck Lump ?Cancer. Coin-shaped on one side of neck. Dad died of cancer. Examination findings: Swelling is 1x1 cm, hard and fixed. Note- display emotions when you receive bad news.$$',
        gender: 'male'
      },
      {
        id: 'varicose-veins',
        name: 'Varicose Veins',
        description: 'You are FY2 in GP. Mrs. Amelia Arden, aged 42, has come to you with painful swelling in both of her legs. Talk to her about her symptoms and explain the treatment options.',
        extraNotes: '$$Varicose Veins. Hairstylist. Stands 8 hrs daily. family hx in sister. Patient just had a baby 4 months ago. Leg swelling during pregnancy.$$',
        gender: 'female'
      },
    ]
  },


  {
    id: 'ortho',
    name: 'Orthopaedics',
    icon: <GiBrokenBone />,
    topics: [
      {
        id: 'rh-arthr',
        name: 'Rh Arthr',
        description: "You are FY2 in GP. Maria, aged 50, has presented with a complaint of hand pain. She is a known smoker for the past 20 years. Take history, assess her, and discuss management with her.",
        extraNotes: 'Rheumatoid Arthritis.',
        gender: 'female'
      },
      {
        id: 'gout',
        name: 'Gout',
        description: "You are an FY2 in GP. James Horton, aged 45, has come to you with the complaint of pain in the big toe. He is hypertensive and on Amlodipine and Bendroflumethiazide. Talk to him, address his concerns, and discuss the management with him.",
        extraNotes: 'Gout.',
        gender: 'male'
      },
      {
        id: 'pmr',
        name: 'PMR',
        description: "You are F2 in GP. Mrs. Olivia, aged 68, presented to the clinic with pain and aches. She was diagnosed with GE reflux 20 years ago and was taking Omeprazole. Please talk to the patient, discuss your initial plan of management, and address her concerns.",
        extraNotes: 'PMR - Pain & Aches.',
        gender: 'female'
      },
      {
        id: 'carpal-ts',
        name: 'Carpal TS',
        description: "You are an F2 in GP. Lucy, aged 34, came to the clinic with pain in both her wrists and hands. Please talk to the patient, discuss the plan of management, and address her concerns.",
        extraNotes: 'Carpal Tunnel Syndrome. You work as a typist in an office. Gave birth 1month ago.',
        gender: 'female'
      },
      {
        id: 'dequervteno',
        name: 'DeQuervTeno',
        description: "You are an FY2 in GP. A patient presents with pain in the thumb. Talk to the patient, assess him, and discuss the management plan.",
        extraNotes: 'De Quervain\'s Tenosynovitis. Plays tennis',
        gender: 'male'
      },
      {
        id: 'raynd-phen',
        name: 'Raynd Phen',
        description: "You are FY2 in GP clinic. Mr. Smith, aged 35, presented with pain in his fingers. Take history, assess him, and manage accordingly.",
        extraNotes: 'Raynaud Phenomenon.',
        gender: 'male'
      },
      {
        id: 'rx-arthr',
        name: 'Rx Arthr',
        description: "You are F2 in Orthopaedics. Mr. Peter Randal, aged 27, came to the hospital with joint pain. Please take history, do a relevant examination, and discuss the management with the patient.",
        extraNotes: 'Reactive Arthritis. Hx of eye pain. Unprotected sexual intercourse at France 3wks ago.',
        gender: 'male'
      },
      {
        id: 'fall-amp-#',
        name: "Fall + #",
        description: "You are F2 in Surgery. Mrs. Sharon, aged 78, has recently had a fall and fractured the neck of her femur. She has undergone surgery. Your consultant has asked you to take history and find out the possible cause behind the fall. Please talk to the patient, take history, find out the cause of the fall, and discuss the management plan with the patient.",
        extraNotes: 'Fall & Hip Fracture. fall 4 days ago. surgery 2 days ago. \'Doctor, my husband and I love cooking. We were in the kitchen making lunch together. I was standing and talking to my husband and I suddenly fell.\' can\'t remember during the fall. after fall ambulance brought her to hospital with right hip fracture. known HTN and arthritis on meds. Candidate is not expected to know the specific cause of the fall but rather to explore differential diagnoses: Postural hypotension, Anemia, Confusion (UTI, Pneumonia, Constipation), Cardiac arrhythmias, DM, HTN, Vision Problems.',
        gender: 'female'
      }
    ]
  },
  {
    id: 'simman',
    name: 'SimMan',
    icon: <AiOutlineRobot />, // Replace with your preferred icon
    topics: [
      {
        id: 'asthma',
        name: 'Asthma',
        description: 'You are F2 in A&E. James, aged 40, came with acute shortness of breath. Please talk to the patient, assess his condition, examine him, and discuss the initial plan of management with the examiner. A Simman is in the station.',
        extraNotes: 'Simman - Acute Exercebation of Asthma (Severe).',
        gender: 'male'
      },
      {
        id: 'anaphylxs',
        name: 'Anaphylxs',
        description: 'You are F2 in surgery. A 55-year-old patient had undergone surgery for a burst appendix. The surgery team decided to give two units of blood. After receiving one unit, while receiving the second unit, the patient experienced shortness of breath. Your nurse colleague is concerned and asked you to talk to the patient. Please talk to the patient, assess him, and do the relevant management. A Simman is in the station.',
        extraNotes: 'Simman - Anaphylaxis.',
        gender: 'male'
      },
      {
        id: 'uti-turp',
        name: 'UTI TURP',
        description: 'You are F2 in A&E. A 75-year-old patient was brought to the hospital by his wife because he was feeling sick. Please talk to the patient and address his concerns. A Simman is in the station. Monitor:  ECG - Normal Pulse - 110/min  Blood Pressure - 100/70 mmHg  SPO2 - 97%  Temperature - 38°C. A Simman is in the station.',
        extraNotes: 'SIMMAN - UTI after TURP. TURP 4days ago. Urethral catheter draining purulent urine.',
        gender: 'male'
      },
      {
        id: 'hypoglyc',
        name: 'Hypoglyc',
        description: 'You are an FY2 in A&E. Samuel, aged 40, is in the hospital drowsy. Assess him and manage accordingly. A Simman is in the station.',
        extraNotes: 'SIMMAN - Hypoglycaemia. Blood sugar \u2013 1.8',
        gender: 'male'
      },
      {
        id: 'sepsis',
        name: 'Sepsis',
        description: 'You are an F2 in the Medicine Department. Mrs. Rachel Thompson, aged 78 years, was admitted to the hospital with a UTI 3 days ago. The nurse has called you, stating that the patient is feeling poorly and that you are the only doctor available to see her. Talk to the patient, assess her, and manage her situation appropriately. A Simman is in the station.',
        extraNotes: 'SIMMAN - Post UTI Septic Shock. Initial Monitor: RR 24 SpO2 88 Pulse 96 BP 105/65 Temp 39.0',
        gender: 'female'
      },
      {
        id: 'ac-conf-st',
        name: 'Ac Conf St',
        description: 'You are an F2 in Accident and Emergency. David Knowles, an 80-year-old, has been brought in by his daughter due to confusion. Talk to the daughter and address her concerns. A Simman is in the station.',
        extraNotes: 'SIMMAN - Acute Confusional State in Elderly secondary to sepsis with focus untreated UTI.',
        gender: 'male'
      },
      {
        id: 'ugib',
        name: 'UGIB',
        description: 'You are an F2 in A&E. Mr. John, aged 68, was brought into the A&E by his wife because he is feeling dizzy and faint. Talk to the patient, assess him, and discuss the initial plan of management with him. A Simman is in the station.',
        extraNotes: 'SIMMAN - Upper GI Bleed.',
        gender: 'male'
      },
      {
        id: 'afib',
        name: 'AFib',
        description: 'You are F2 in A&E. Julia, aged 52, came with dizziness. Please talk to the patient, assess her condition, examine her, and discuss the initial plan of management with the examiner. After 6 minutes, discuss the plan of management with the patient. A Simman is in the station.',
        extraNotes: 'SIMMAN - Dizzy Spells (Atrial Fibrillation).',
        gender: 'female'
      },
      {
        id: 'ac-lmb-isch',
        name: 'Ac Lmb Isch',
        description: 'You are an F2 in A&E. A 60-year-old female presented with a history of severe pain in her right leg since this morning. Talk to her, take history, assess the patient, perform a relevant examination, and discuss the management. There is a simwoman in the station.',
        extraNotes: 'SIMWOMAN - Acute Limb Ischaemia.',
        gender: 'female'
      },
      {
        id: 'pph',
        name: 'PPH',
        description: 'You are an F2 in O&G. Mrs. Jefferson, aged 35 and multigravida, had her 5th delivery an hour ago and is bleeding. The nurse called you. Assess the patient and perform the initial management. There is a nurse in the cubicle to assist you. There is a simwoman in the station.',
        extraNotes: 'Postpartum Hemorrhage (PPH 2 Uterine Atony). In hypovolaemic shock. Initial Monitor: SpO2: 91 RR: 26 BP: 90/60 Pulse: >100',
        gender: 'female'
      },
      {
        id: 'conscious',
        name: 'Conscious',
        description: 'You are F2 in A&E. Mr. Andy Charles, aged 45 years, fell from a height of 2 meters onto a concrete slab. The patient is on resus trauma. Assess the patient and discuss the initial management with the examiner. After 6 minutes, present your findings and initial management to the examiner. A simman is in this station.',
        extraNotes: 'SIMMAN - Primary Survey Conscious. Fall. Pelvic fracture. Complaint - pain (points to pelvic area)',
        gender: 'male'
      },
      {
        id: 'unconscious',
        name: 'Unconscious',
        description: 'You are F2 in A&E. Mr. Andy Charles, aged 45 years, fell from a height of 2 meters onto a concrete slab and is currently unconscious in resus trauma. Assess the patient thoroughly, ensuring you follow the ABCDE approach, and then discuss the initial management plan with the examiner. After 6 minutes, confidently present your findings and management plan to the examiner. A simman is in this station.',
        extraNotes: 'SIMMAN - Primary Survey Unconscious. Fall from Ladder. Head Injury. Unresponsive.',
        gender: 'male'
      }
    ]
  },
  {
    id: 'angry',
    name: 'Angry',
    icon: <BiAngry />, // Replace with your preferred icon
    topics: [
      {
        id: 'op-wound',
        name: 'Op Wound',
        description: 'You are F2 in A&E. Mr. Albert Green, aged 50, presented to the hospital with pain and swelling at the site of his operation. He had an open operation for his right inguinal hernia three weeks ago, which was a day care surgery. The nurse told you that the patient is angry. Please talk to the patient, assess him, discuss the management, and address his concerns.',
        extraNotes: 'Post-Op Wound Infection. Patient is angry and blaming the surgeons and hospital for the wound. however, patient started working within 1 week of operation. Also smoking. Has smoker\'s cough too. CONCERNS: P: Doctor is it possible to catch this bug from the hospital? P: Doctor is it happening a lot in your hospital? P (if told about admission): I have to work. Who will look after my wife and kids? P: Doctor I want to complain. I am not happy. P: Doctor, I don\u2019t want this to happen to anyone else. P: I am still not happy. I want to complain.',
        gender: 'male'
      },
      {
        id: 'iv-cannula',
        name: 'IV Cannula',
        description: 'You are F2 in A&E. Mrs. Sarah River, aged 50, presented with cellulitis from an insect bite 48 hours ago. Your consultant decided on IV antibiotics for 24 hours. The patient is upset and wants to talk to you. Address her concerns, including issues related to the IV cannula and any interactions with FYI Doctor Wilson, who recently started her training.',
        extraNotes: 'This station is for procedures. The doctor must verbally run-through the steps for the procedure in order to pass. IV Cannulation. c/o \'Doctor I am in pain. I had an operation for my appendix, give me some pain killer\'. also vomiting. if they are asking too many questions (apart from PMHx, Medication Hx & Allergy Hx), refuse to answer until they give you the IV paracetamol and Metoclopramide. wince in pain if they don\'t warn you about \'sharp scratch coming\'. EXAMINER: Provide Chart when candidate requests: 5 mg IV Morphine every 4 hours. Last dose received 1 hour ago. Vitals: RR: 12-20/min. Temperature: 38, PR: 94, O2 sat: 92% to 94%, BP: 120/80. Examiner asks: 1. what are you going to do for the patient? after candidate answers, 2. what if pain continues?',
        gender: 'female'
      },
      {
        id: 'late-xray',
        name: 'Late Xray',
        description: 'You are an FY2 in Medicine. Mrs. Daniella White, aged 67, was diagnosed with lung cancer after presenting with breathlessness. She previously visited the hospital with a cough and was treated for a lung infection. Her son is upset, feeling that the diagnosis was delayed. Address his concerns, explain the situation, and discuss how you will manage her care moving forward.',
        extraNotes: 'Angry relative \u2013 Lung Cancer. Complaints: \'Doctor, I am extremely upset as the GP missed lung cancer in my mother.\' If doctor asks for initial complaints \'She came to the clinic with cough with phlegm and was give antibiotics. She didn\u2019t improve even after continuing the medications. Then she developed breathlessness and was diagnosed with lung cancer.\' If doctor asks for the current state say \'she was fine before but is now getting worse.\' If doctor ask for the investigations done previously say \'My mother has been to the clinic previously and x-ray was not done. Only some blood tests were done. Now she has been diagnosed with cancer and nothing can be done.\' If doctor asks whether you informed the GP that her cough isn\u2019t getting better despite the medicines say \'My mother took antibiotics which was prescribed to her for her chest infection and there was no improvement in her health. Then she visited again to the GP and was referred to the specialist for further management.\' If doctor tries to justify the delayed xray by saying the initial features were those of chest infection say \'Okay. I am still not happy with the way the GP handled everything related to my mother. If she was your mother how would you feel?\' If the doctor tries further justification, say \'I want to make a complaint.\' show your anger in your statements.',
        gender: 'female'
      },
      // Continue with remaining entries following same pattern
      {
        id: 'fb-oesoph',
        name: 'FB Oesoph ☎️',
        description: 'You are F2 in A&E. Three-year-old Amy was brought in after choking, and you initially reassured her mother and discharged Amy. After receiving the X-ray report indicating a button in the oesophagus, call her mother on the telephone to explain the situation. Ask her to bring Amy back for further assessment, emphasizing the importance of timely intervention. Be prepared to discuss the next steps in management once they arrive.',
        extraNotes: 'Medical Error - Missed Diagnosis - Foreign Body (Button in Oesophagus). Angry Patient mother when she hears the error. Initially refuse to come to the hospital but later agree. TELEPHONE CONVERSATION so ask who\'s speaking when you get the call.',
        gender: 'female'
      },
      {
        id: 'chng-cnsllr',
        name: 'Chng Cnsllr',
        description: 'You are F2 in GP. Talk to the 30-year-old patient who is undergoing counselling for depression and taking sertraline. She is requesting to change her counsellor. Address her concerns and discuss her options.',
        extraNotes: 'Changing Counsellor. Complaints: \'I want to change my counsellor.\' \'When I was pregnant my husband left me for someone else and I had to abort the baby. I was in depression and I am taking medications also. I was seeing him for that purpose.\' \'I started taking medication 2 years back.\' \'I am fine.\' \'Yes.\' \'No.\' \'I was taking the counselling sessions from last one year.\' \'I was going to the same counsellor.\' \'I prefer a female counsellor.\' \'Sometimes he touches and hugs me. There was some intimacy between me and my counsellor.\' \'No, it was Consensual from my side as well. This is not his fault, I use to encourage him and we went out for few dates. But then I came to know he has a girlfriend. I have no complaints against him. I just want a female counsellor. Please change my counsellor.\' \'I don\u2019t have any problems against my previous counsellor. I don\u2019t want him to get into trouble, he is a nice man.\'',
        gender: 'female'
      }
    ]
  },
  {
    id: 'obgyn',
    name: 'OBGYN',
    icon: <MdPregnantWoman />,
    topics: [
      {
        id: 'pre-eclmps',
        name: 'Pre-Eclmps',
        description: "You are F2 in OBG. Laila, aged 29 years old and 36 weeks pregnant, has been referred to the hospital. The midwife has seen her and noted her vitals: BP 160/110, Urine protein - (+++). Her BP in the first booking was 110/70. Please talk to the patient, assess her condition, discuss your further management with her, and address her concerns.",
        extraNotes: 'Pre-eclampsia. Complaint: \'I went for my check up and I was sent here by the midwife because of my blood pressure and protein in my urine.\' No symptoms. Refuse admission if doctor offers until he convinces you. Say you want to have a pool delivery at home.',
        gender: 'female'
      },
      {
        id: 'gonorrhea',
        name: 'Gonorrhea',
        description: "You are F2 in the GUM clinic. Mrs. Juliet, aged 24, has come to the clinic for her investigation results. The swab taken shows gonorrhoea. Please talk to the patient, take her sexual history, inform her about the results, discuss further management, and provide advice.",
        extraNotes: 'STD - Gonorrhoea. Complaint: \'I got a sexual check up done as I got to know about sexually transmitted infections and I am here for my results.\' PATIENT\'S CONCERNS: P: Is Gonorrhoea a serious condition? P: How are you going to treat me Dr.? P: How did I get this condition? P: Did I get it from my partner? P: Is he cheating on me? P: What are the complications of Gonorrhoea?',
        gender: 'female'
      },
      {
        id: 'ecto-preg',
        name: 'Ecto Preg',
        description: "You are F2 in the OBG Department. Janny, aged 18, presented to the hospital with left iliac fossa pain and a positive pregnancy test after six weeks of amenorrhea. Please talk to the patient, take relevant history, and discuss the different steps of management with her.",
        extraNotes: 'Ectopic Pregnancy. Show alot of concerns. Refuse admission and surgery initially until doctor convinces you. Refuse the doctor informing your parents.',
        gender: 'female'
      },
      {
        id: 'miscarrg',
        name: 'Miscarrg',
        description: "You are F2 in the Antenatal Clinic. Ema, aged 28, is referred by her GP for her first ANC check-up. She is 6 weeks pregnant. The nurse has examined the patient, urine test has been done, and vitals have been checked: BP - 130/80, Pulse - 70, Urine Test Negative (no infection & protein). Talk to the patient, take relevant history, do the initial assessment, and address her concerns.",
        extraNotes: 'Normal ANC checkup for early pregnancy. Patient has had 2 previous Miscarriage. And is concerned about having a miscarriage in this pregnancy. Wants to know the chances of that happening and what to do to prevent it. Past hx of smoking and drinking. stopped when she found out she was pregnant.',
        gender: 'female'
      },
      {
        id: 'pcos',
        name: 'PCOS',
        description: "You are FY2 in GP clinic. Ms. Kim, aged 29, has come to the GP Surgery with complaints of Acne and Amenorrhea. On her first visit, a pregnancy test was done and found to be negative. Her hormonal profile was done. Discuss these test results with her, take appropriate history, discuss management, and address her concerns. Hormonal Results: LH - 18 mlU/ml (1-12 mlU/ml), FSH - 6 mlU/ml (1-9 mlU/ml), BMI - 32.",
        extraNotes: 'PCOS.',
        gender: 'female'
      },
      {
        id: 'pms',
        name: 'PMS',
        description: "You are an FY2 in GP. Miss Katie Yale, aged 29, has come to you with some concerns. One week ago, investigations were done in the GP and came back normal. Talk to her and address her concerns.",
        extraNotes: 'Pre-menstrual syndrome (Depo-Provera) Complaints: husband asked me to come to hospital due to monthly mood swings around periods x 7months.',
        gender: 'female'
      },
      {
        id: 'postpart-depress',
        name: "Postpart Depress☎️",
        description: "You are an FY2 in GP. Miss Amanda Lowe, aged 31, called you with a complaint of insomnia. Talk to her and address her concerns.",
        extraNotes: 'Post-Partum Depression. Telephone conversation so relate as such.',
        gender: 'female'
      },
      {
        id: 'rub+rhs',
        name: 'Rub+Rhs',
        description: "You are FY2 in Antenatal clinic. A 32-year-old lady is coming for her antenatal assessment. She is 14 weeks pregnant. Her lab results are: Blood group: O, Rhesus type: Rh -ve, Rubella: Non-immune. All other blood tests like LFTs, RFTs, and U&Es normal. USG: Singleton fetus. Talk to her and address her concerns.",
        extraNotes: 'Routine Antenatal Assessment. When the doctor tells you about the results, show concerns about Rubella and Rhesus. Ask about meaning, implications, options, vaccines, prevention and etc.',
        gender: 'female'
      },
      {
        id: 'contrcptn',
        name: 'Contrcptn',
        description: "You are F2 in GP. A 29-year-old female patient presents to the hospital to get some advice about contraception. Talk to the patient, take relevant history, and discuss different methods of contraception.",
        extraNotes: 'Contraception Discussion. Patient wants to know about contraception methods. Reason: she just changed boyfriend and the new one doesn\'t like condoms. Patient is particularly interested in COC pills. Past hx of blood clots on the leg (DVT). Show concerns and ask relevant questions. Doctor must recommend POP instead of COCP to pass this station.',
        gender: 'female'
      },
      {
        id: 'ramipril',
        name: 'Ramipril',
        description: "You are an FY2 in the GP Surgery. Mrs. Amy Travis, aged 42, has come to see you. She is on Ramipril for her hypertension. Talk to her and address her concerns.",
        extraNotes: 'Pre-Pregnancy Counselling (HTN on Ramipril). Patient has never been pregnant. D: How can I help you? P: I am trying to get pregnant.',
        gender: 'female'
      },
      {
        id: 'preg-@16',
        name: 'Preg @16',
        description: "You are an FY2 in GP. Miss Leanne Bailey, aged 16, has booked an emergency appointment. Talk to her, assess, and manage her concerns.",
        extraNotes: 'Pregnancy (16 yo). Complaints: Vomiting. Other symptoms negative. Only reveal you have missed your period by 1week if doctor asks about LMP. You have a 17yr old boyfriend. Show concerns and denial. Refuse to inform your parents.',
        gender: 'female'
      }
    ]
  },
  {
    id: 'pedo',
    name: 'Pediatrics',
    icon: <BiChild />,
    topics: [
      {
        id: 'pneumonia-telephone',
        name: 'Pneumonia☎️',
        description: "You are an F2 in Paediatrics. 10 months old baby has been sick for 2 days and is on triage care call. His mother, Mrs. Maria is concerned. Talk to the mother, discuss the initial plan and address her concerns. (Mother is very worried and concerned)",
        extraNotes: 'CHILD TELEPHONE CONVERSATION \u2013 FEVER, SUSPECTED CHEST INFECTION. D: Hello. (Introduce yourself). Confirm the mother\u2019s name, child\u2019s name and age. M: Dr., can you please come and see my baby immediately, he is ill. D: I am so sorry to hear that. We are here to help your baby. Could you please tell me what exactly happened? M: My baby has fever for 2 days and he is not feeling well. D: Have you measured the temperature? M: Yes doctor. I have measured it with my home thermometer, and it was 39 C. D: Have you done anything for it? M: I gave him paracetamol, but it has not improved. D: Have you noticed any other symptoms? M: Like what doctor? D: Any cough? M: Yes, since yesterday. D: Any phlegm along with cough? M: No doctor. D: Any difficulty in breathing? M: Yes. He has difficulty in breathing since yesterday. D: Any rashes over the body? M: No D: Any difficulty in moving his neck? M: No D: Does he shy away from light? M: No D: Any headache? M: No. D: Any ear pain or discharge? M: No D: Any vomiting? M: No D: Any diarrhoea or loose stools? M: No D: Is he passing urine normally? M: No, I haven\u2019t changed his diaper since yesterday. D: Any tummy pain? M: No D: Is your baby active? M: No doctor, he is lethargic and tired. D: Has your baby been diagnosed with any medical conditions in the past? M: No D: Is he on any regular medications? M: No D: Any family member with any medical problems? M: No D: Any family member with any similar complaint? M: No D: How was the birth of your baby? D: Are you happy with the red book? M: Yes D: Is he up-to-date with all his jabs? M: No D: Did he receive any recent jab? M: It was a normal delivery D: Is he feeding well? M: No doctor he hasn\u2019t been feeding well since yesterday. D: Have you travelled anywhere with your child recently? M: No D: Who looks after your baby? M: I do. D: Thank you for answering all my questions. I suspect your baby might be having chest infection. He needs immediate admission. M: What should I do now doctor? D: Don\u2019t worry, I am going to send an ambulance to get your baby to the hospital. We will have to examine him, do some blood test and a chest x-ray. If we find it is an infection, we will give him antibiotics. M: Ok Dr. When will the ambulance arrive? D: We will send the ambulance immediately. Hopefully, it should reach you soon. M: Ok Dr. D: Do you have any other concerns? M: No D: Thank you.',
        gender: 'female'
      },
      {
        id: 'feb-conv',
        name: 'Feb Conv',
        description: "You are F2 in Pediatrics. Maria, aged 2, has been brought to the hospital by her mother, Mrs. Diana Julie, after having a fit. She has been managed in A&E and referred to you. Her temperature is 38.9°C, and there is redness over her left eardrum on examination. Talk to the mother, take history, discuss your management plan, and address her concerns. The child is not in the cubicle, and the mother is very concerned.",
        extraNotes: 'Febrile Convulsion. PATIENT\u2019S CONCERNS: If doctor mentions admissions ask \'Can I take my baby home?\' If doctor mentions diagnosis ask \'Is it dangerous?\' \'Is it epilepsy?\' Later ask \'Can it happen again in the future?\' \'How can I prevent it?\' \'What should I do if it happens again?\'',
        gender: 'female'
      },
      {
        id: 'nai',
        name: 'NAI',
        description: "You are F2 in Pediatrics. 4-month-old Daniel has been brought to the hospital by his mother, Mrs. Diana Wales, after she noticed a swelling on his right arm. X-ray confirmed a spiral fracture of the right humerus. Talk to the mother, take history, and discuss the initial management plan with her. Your nurse colleague is attending to the child in the next room. The mother is very worried.",
        extraNotes: 'Non-Accidental Injury (NAI) - Spiral Fracture. Mother is a nurse- went on night shift yesternight when it happened. The child was with the stepfather ie mother\'s partner. When the doctor suspects NAI appear to be in denial. Also show concerns about whether social services will take your child away.',
        gender: 'female'
      },
      {
        id: 'mmr-autism',
        name: 'MMR Autism',
        description: "You are F2 in GP. Mrs. Gabriela Evans, Jenny's mother, has come to the GP surgery to discuss her 12-month-old daughter's upcoming MMR vaccination, which is scheduled in two weeks. You haven't seen Jenny since birth. Talk to the mother and address her concerns. This is a non-emergency visit.",
        extraNotes: 'MMR Vaccination. Complaint: \'Doctor. I read an article which says there is a link between MMR and autism. Is this true?\' Other concerns the patient must ask one by one: \'Doctor, what is MMR? What is the use of this vaccine?\' \'How many shots of this jab should be given doctor?\' \'Why do we give MMR vaccine doctor?\' \'Doctor, are these diseases still present in the UK? / Doctor, these diseases are not that common in the UK, so why my little one should receive this vaccine?\' \'Doctor, you said there were cases of measles in the UK in recent years. When was the last time?\' \'Doctor, is there an alternative to MMR?\' \'Does MMR cause any problems with the gut?\' \'Any contraindications to it?\'',
        gender: 'female'
      },
      {
        id: 'intssptn',
        name: 'Intssptn',
        description: "You are F2 in Pediatrics. A 20-month-old child was brought to the hospital by his mother due to abdominal pain. The GP has sent the child to the hospital with a letter describing abdominal discomfort. On examination, the child appears pale, with cold peripheries and lethargy. The abdomen is not distended, but a mass is noted in the right hypochondrium. Vitals are PR 140 bpm, BP 90/60 mmHg, and RR 28. Please talk to the mother, take a history, give her a possible diagnosis, explain the next steps of management, and address her concerns. The baby is in the next room.",
        extraNotes: 'Intussusception.',
        gender: 'female'
      },
      {
        id: 'pyl-sten',
        name: 'Pyl Sten',
        description: "You are F2 in Pediatrics. A 6‑week‑old baby has been brought to the hospital by her mother due to vomiting. Vitals are as follows: Pulse 140 bpm, Respiratory rate 40, BP 100/70 mmHg, SpO2 96%, and Temp 36.8°C. Please talk to the mother, take a history, discuss the plan of management, and address her concerns. The baby is not in the cubicle.",
        extraNotes: 'Pyloric Stenosis.',
        gender: 'female'
      },
      {
        id: 'n-terrors',
        name: 'N Terrors',
        description: "You are an F2 in GP. Mrs. Jane Williams, a 28‑year‑old, has come to speak to a doctor about her daughter, expressing concern. Please talk to the mother and address her concerns. The daughter is not with the mother.",
        extraNotes: 'Night Terrors. She has no memory of the event.',
        gender: 'female'
      },
      {
        id: 'autism',
        name: 'Autism',
        description: "You are a F2 in GP. The mother of a 3‑year‑old child, Jason, is concerned about her son. Talk to her and address her concerns.",
        extraNotes: 'Autism. D: How can I help you? P: I am very concerned about my son. D: May I know what your concern is? P: His health assessor is worried that Jason is not interacting well with other children at the nursery.',
        gender: 'female'
      },
      {
        id: 'primary-enuresis',
        name: '1*Enuresis',
        description: "You are an FY2 in GP Surgery. Rachel Williams, the mother of 4‑year‑old David Williams, has brought him to the clinic because of bed wetting. Talk to her and address her concerns.",
        extraNotes: 'Primary Enuresis.',
        gender: 'female'
      },
      {
        id: 'conduct-disorder',
        name: 'Conduct Disorder',
        description: "You are an FY2 in GP. Lisa James, aged 30, is concerned about her 4‑year‑old daughter. Talk to her and address her concerns.",
        extraNotes: 'Conduct Disorder - Negativism. Complaint: \'my child doesn\'t listen to me. She started her nursery 2 weeks ago.\' If doctor asks for more say \'She is always playing with her food. She is taking roughly 30 mins to go to bed.\'',
        gender: 'female'
      },
      {
        id: 'dev-miles',
        name: 'Dev Miles',
        description: "You are FY2 in Paediatrics. Hannah Williams, mother of Jenny Williams aged 15 months, comes to the hospital with a concern that her child is not able to walk. Ask relative questions and address her concerns.",
        extraNotes: 'Child Developmental Milestones - normal. The mother should show a lot of concerns and ask a lot of questions. However, everything is normal. The doctor must reassure her.',
        gender: 'female'
      }
    ]
  },
  {
    id: 'bbn',
    name: 'Breaking Bad News',
    icon: <FaSadCry />,
    topics: [
      {
        id: 'postop-bleed',
        name: 'Postop Bleed',
        description: "You are F2 in Vascular Surgery. Mrs. Janet aged, 65 has undergone an aortobifemoral bypass graft in her lower limb due to vascular insufficiency that caused calf pain. She was shifted to the recovery room, after the operation. Your nurse colleague noticed that she was bleeding heavily into the drain, a few hours after the operation. She was given six units of blood products. Your colleagues all scrubbed in to the theatre. She was taken to the theatre for re-exploration. Her husband has come to the hospital to see his wife. Please talk to him about his wife’s condition and address his concerns. There was no error in surgery. This is a known complication of the surgery. Consent from Mrs. Brown has been taken to talk to her husband.",
        extraNotes: 'POST-OP BLEEDING. Aortobifemoral Bypass. Patient Concerns: \'Yes Dr.\' \'I came to see my wife. Where is she? How is she Dr.?\' \'Ok Dr.\' \'No. I don\'t know what\u2019s happening.\' \'She had her operation this morning. I came to see her but she was not in the recovery room.\' \'Bleeding? Is it serious Dr.?\' \'What? 6 units. Isn\u2019t it too much?\' \'Will she be okay doctor?\' \'Have you guys done a mistake during the surgery?\' \'How can she lose so much of blood. I am sure something might have gone wrong during the surgery.\' \'I wasn\'t aware of this. No one told me this.\' \'I have lost faith in you guys. Are you sure that it wasn\'t an error?\' \'What\'s the difference between an error and a complication?\' \'How can you convince me that what happened to my wife was a complication and not an error?\' \'What is the success rate of this operation?\' \'I didn\'t know about any of this. If it was that risky, why would she go for it?\' \'Doctor, what can I do now? Doctor, shall I go home or shall I wait in the waiting area?\' \'Doctor, shall I call my children to come and say goodbye to their mother?\' \'Doctor, my son lives in Australia and my daughter lives in London. I\u2019m sure they would want to see their mom for the last time.\' \'I am the only one who looks after her. How long will she be here for?\' \'Doctor, are there any other complications?\' \'How will her situation be afterwards?\'',
        gender: 'female'
      },
      {
        id: 'brain-bleed',
        name: 'Brain Bleed',
        description: "You are F2 in A&E. Mr. Ali aged, 78, has collapsed and brought to the hospital with loss of consciousness by an ambulance. The initial survey has been done. The CT Scan has been done and shows a massive intra cerebral bleeding due to ruptured berry aneurysm. The neurology team has seen the patient and reviewed his CT scan. They decided that surgery cannot be done. He is now breathing independently/unassisted. Patient is unconscious. Please talk to his wife Mrs. Maria Ali, explain that her husband’s condition and address her concerns. Patient is in terminal condition and only palliative care is possible. Patient’s wife does not know about her husband’s condition.",
        extraNotes: 'BBN CEREBRAL BLEEDING. \'Yes Dr.\' \'Doctor my husband was absolutely fine. He was watching TV and suddenly he got a bad headache and then he collapsed. I called the ambulance and we rushed to the hospital. Doctor, how is he now?\' \'He is having high blood pressure but he is taking medication for that.\' \'No\' \'No\' \'No. doctor. Please tell me.\' \'Doctor I came to the hospital alone, please tell me what happened?\' \'But doctor, how could it be possible? He only had blood pressure (+/- and Diabetes), which was well controlled. He always takes his medications.\' \'No\' \'Doctor, what are you going to do now? Can you do an operation for him?\' \'Doctor, does this mean that there is nothing you can do for him?\' \'So what are you going to do for him?\' \'Doctor, are you going to shift him to ICU?\'',
        gender: 'female'
      },
      {
        id: 'isch-stroke',
        name: 'Isch Stroke',
        description: "You are an F2 in Surgery. Mr. David aged 65, had an Ischaemic Stroke last week which affected his left side. He was recovering well and doing fine. He developed another stroke yesterday and his GCS is 3 now. They did CT scan and it showed massive clot in both the hemisphere. Only Analgesics and IV Fluids can be given to the patient. If patient deteriorates then Do Not Resuscitate the patient. His daughter came to you to discuss her father’s condition. Please talk to the daughter, explain her father’s condition and address her concerns. Daughter is pregnant. MDT has decided that surgery cannot be done and they have decided only palliative care is possible.",
        extraNotes: 'BBN TALK TO DAUGHTER. \'I\'m Ok Doctor, how is my father.\' \'He had a stroke but he was doing fine after that. He was recovering well.\' \'No, tell me what happened?\' \'No Dr. I came to the hospital alone, please tell me what has happened?\' \'What are you going to do now?\' \'Dr does this mean that you cannot do anything for him?\' \'So, what are you going to do for him?\' \'How much time does he have Dr?\' \'Dr Can you put him on ventilator as I\u2019m due for my delivery in the next 2 weeks?\' \'Can I see my dad?\'',
        gender: 'female'
      },
      {
        id: 'edh-josh',
        name: 'EDH Josh',
        description: "You are F2 in Pediatric Emergency. Joshua, aged 9 had a Road Traffic Accident an hour ago. Initial survey has been done and there is a head injury. No other injury has been found. CT Scan has been done and showed an extradural hemorrhage. You have not seen the patient. A team of doctors is resuscitating the child and planning to take him to the theatre. Please talk to the parents; Mr. and Mrs. Barnes and address their concerns.",
        extraNotes: 'Extradural Hemorrage. \'Doctor will he die?\' \'How is my son?\' \'We don\'t know; is he going to be ok?\' \'We were out for a picnic. Josh and I were waiting for his dad and his twin brothers outside a restaurant. When Josh saw them he got excited and ran towards them. I heard a loud noise and I ran to see what happened. I noticed a car had hit him and my Josh was on the street.\' \'It was my mistake that I was not holding Joshua\u2019s hand.\' \'Doctor, it took more than twenty minutes till the ambulance came.\' \'There was blood everywhere on the street.\' \'Doctor, we panicked and we didn\'t notice. I think from his head.\' \'Doctor, he was unconscious\' \'Doctor, will he die?\' \'Will he be okay?\' \'No doctor, please tell us.\' \'Doctor, will he be ok?\' \'Will he die?\' \'Surgery! Which surgery?\' \'Doctor, can we see him?\' \'No doctor, we want to see him now.\' \'Are there any complications for this surgery?\' \'Are there any long-term complications?\'',
        gender: 'male'
      },
      {
        id: 'pelvic-#',
        name: 'Pelvic #',
        description: "You are F2 in A&E. Joshua aged 9 had a road traffic accident. He was brought to the hospital and initial survey was done. He had an unstable pelvic injury. He has lost a lot of blood and he is hypotensive. Initial management was done and patient has been resuscitated. He is being prepared to go for a surgery. Parents are quite anxious. Talk to the parents, discuss about further management and address concerns.",
        extraNotes: 'PELVIC FRACTURE. \'Doctor will he die?\' \'How is my son?\' \'We don\'t know; is he going to be ok?\' \'We were out for a picnic. Josh and I were waiting for his dad and his twin brothers outside a restaurant. When Josh saw them he got excited and ran towards them. I heard a loud noise and I ran to see what happened. I noticed a car had hit him and my Josh was on the street.\' \'It was my mistake that I was not holding Joshua\u2019s hand.\' \'Doctor, it took more than twenty minutes till the ambulance came.\' \'There was blood everywhere on the street.\' \'Doctor, we panicked and we didn\'t notice. I think from his head.\' \'Doctor, he was unconscious\' \'Doctor, will he die?\' \'Will he be okay?\' \'No doctor, please tell us.\' \'Doctor, will he be ok?\' \'Will he die?\' \'Surgery! Which surgery?\' \'Doctor, can we see him?\' \'No doctor, we want to see him now.\' \'Are there any complications for this surgery?\' \'Are there any long-term complications?\'',
        gender: 'male'
      },
      {
        id: 'post-mortem',
        name: 'Post Mortem',
        description: "You are F2 in Respiratory Dept. Mr. Davis aged 60, was brought to the hospital because of cough, four days ago and was diagnosed with chest infection. He passed away yesterday. The most probable cause of death is respiratory failure due to chest infection. Please talk to his wife, Mrs. Sarah and address her concerns. Post mortem has been considered to determine the cause of death. Death certificate can be issued.",
        extraNotes: 'POST MORTEM. \'My husband died yesterday and I am not quite sure why this has happened. I have got some concerns about the death of my husband.\' \'Yes, but I didn\u2019t quite understand.\' \'What is it?\' \'But he was fine and it was the first time he was admitted to any hospital. He has never even smoked. How could he have respiratory failure?\' \'He was absolutely fine and all this happened suddenly while he was here. I talked to my friend who is a nurse and she told me that we can find out the exact cause of his death by doing some procedure. I don\u2019t know what l have to call that procedure?\' \'Yes.\' \'Thank you so much doctor. How long does it take to be done?\' \'I want to arrange for his funeral.\' \'Could you please tell me how are you going to do post-mortem?\' \'Could you please tell me how are you going to do post-mortem?\' \'Doctor, will the body be disfigured?\' \'Okay doctor, that\u2019s fine.\' \'Ok.\' \'Doctor. I need to think about it.\'',
        gender: 'female'
      }
    ]
  },
  {
    id: 'ent',
    name: 'ENT',
    icon: <TbEar />, // Replace with appropriate icon from react-icons
    topics: [
      {
        id: 'sinusitis',
        name: 'Sinusitis',
        description: "You are FY2 in GP surgery. Mr. James Rickman presented with pain in forehead and cheeks started a few days ago. Talk to him, take history and address his concerns.",
        extraNotes: 'Sinusitis.',
        gender: 'male'
      },
      {
        id: 'bell-pal',
        name: 'Bell Pal',
        description: "FY2 in GP Surgery. Mr. Alexander aged 40 years presented to you with a complaint of drooping of the mouth. Take a history and manage him accordingly. Or Mr. Alexander aged 40 years old presented with some concerns. Take history and assess him accordingly.",
        extraNotes: 'Bell\'s Palsy. One-Sided Facial Drooping x 1day. Concerns: \'how are you going to treat me?\' \'will I be alright after treatment?\' \'how long does Bell\'s palsy last?\' \'How can I prevent Bell\u2019s palsy?\' \'Should I go for brain scan?\' \'Will my face be symmetrical again?\'',
        gender: 'male'
      },
      {
        id: 'mumps',
        name: 'Mumps',
        description: "You are FY2 in GP clinic. A mother of a 9-year-old boy is here with us with some concerns. Talk to her, take history and manage him accordingly.",
        extraNotes: 'Mumps. Complaint: \'Doctor my son has swelling near and below his ear\' \'its increasing in size\' left side. painful. fever before swelling. Concerns: \'how can mumps spread?\'',
        gender: 'male'
      },
      {
        id: 'bppv',
        name: 'BPPV',
        description: "You are an FY2 in GP. Mr. Liam Jackson, aged 45, has come to you with dizziness. Please talk to him, assess him and address his concerns.",
        extraNotes: 'Benign Paroxysmal Positional Vertigo (BPPV).Patient\'s Job: \'Travel agent\'. Patient\'s concerns: \'Is it serious?\' \'Will it go away?\' \'Is there any treatment?\'',
        gender: 'male'
      },
      {
        id: 'vesti-neuri',
        name: 'Vesti Neuri',
        description: "You are F2 in A&E. Harlow, aged 22, was out shopping in a supermarket, she turned her head and had a sudden episode of dizziness. Talk to the patient, assess her condition. Discuss the initial plan of management with the patient.",
        extraNotes: 'Vestibular Neuritis. Patient\'s Job: \'state agent\'. PATIENT\u2019S CONCERNS: \'When can I go home?\' \'Do I need any further tests or treatment?\'',
        gender: 'female'
      },
      {
        id: 'menieres-dx',
        name: 'Menieres Dx',
        description: "You are an FY2 in Medicine. Mr. Benjamin Rao, aged 30, has come to you with a complaint of dizziness. Talk to him, manage and address his concerns.",
        extraNotes: 'Meniere\'s Disease (Dizzy Spells).',
        gender: 'male'
      },
      {
        id: 'tinnitus',
        name: 'Tinnitus',
        description: "You are an FY2 in GP. Mr. Kieran Richards, aged 40, has come to you with a complaint of hearing noises. Talk to him and address his concerns.",
        extraNotes: 'Unilateral Tinnitus. DIfferential DIagnoses',
        gender: 'male'
      },
      {
        id: 'chlsttoma',
        name: 'Chlsttoma',
        description: "You are FY2 in GP. Mr. Josh Andrews, aged 26, came to the clinic complaining of pain in the ear. Talk to the patient and discuss the management with the patient.",
        extraNotes: 'Cholesteatoma. Patient concerns: \'Why did I get it?\' \'can it happen again?\'',
        gender: 'male'
      },
      {
        id: 'acstc-nrm',
        name: 'Acstc Nrm',
        description: "You are F2 in GP. Mrs. Selina Richards, aged 50, presented with a problem in her ear. She has come to you for the first time. Please talk to the patient, take focused history, do necessary examination and discuss your initial plan of management with the patient.",
        extraNotes: 'Acoustic Neuroma (CN VIII).',
        gender: 'female'
      },
      {
        id: 'tonsilitis',
        name: 'Tonsilitis',
        description: "You are an F2 in GP. Samaira, aged 34, came to the clinic with a sore throat. Please talk to the patient, discuss the plan of management with the patient, and address her concerns.",
        extraNotes: 'Acute Bacterial Tonsillitis. Allergic to penicillin',
        gender: 'female'
      },
      {
        id: 'rcrnt-tonsil',
        name: 'Rcrnt Tonsil',
        description: "You are F2 working in GP. Anne, mother of a 6-year-old boy diagnosed with Tonsillitis, has come to you to talk about her son's referral to ENT surgery that was rejected. The child previously had 5 episodes of infections over 6 months. Talk to the mother and address her concerns. On request of the mother, GP made the referral to ENT Surgery.",
        extraNotes: 'Recurrent Tonsillitis. D: How can I help you today? P: I am here for my son. He had recently been referred to ENT surgery from GP and the referral got rejected. D: I am really sorry for your experience. Would you mind if I ask you a few questions regarding your son to have better understanding of your son\'s health? P: Ok D: Could you tell me why he had been referred to the ENT surgery? P: He had 5 episodes of tonsillitis in the last 1 year D: Could you give a brief recap of the episodes? P: The first episode was about 10 months ago. He had sore throat and fever and was advised to have steam inhalation. The 2nd and 3rd episodes the symptoms were more severe, and he was given antibiotics. And the last 2 episodes was like the first episode and it got better with steam inhalation as well. D: I can understand It must be very tough for him. P: Yes, so why did the referral get rejected? D: As you already know the referral was made upon your request. But to be honest with you your son doesn\'t meet the criteria to have the surgery for the tonsil removal. P: What criteria are you talking about? D: I do understand your concern. Let me explain this to you further. There are few criteria that has been set to decide which patients need tonsil removal surgery. One of those criteria is having at least 7 attacks in a year. You mentioned your son had 5 attacks. Possibly that\'s why the referral got rejected. P: Dr. forget about the criteria. I can\'t see him suffer like that. Please arrange the surgery anyhow. D: I can really see you are worried about your son. But let me tell you the criteria are made in a way to avoid unnecessary surgery and ensure better care for the patients. And every surgery has a lot of complications. We don\'t want your son to go through the unnecessary stress of the surgery without any strong reason. Another thing is that tonsils are very important part of the defence mechanism of our body that fight against infection. That is why we don\'t want to remove them unless it\'s necessary. P: Dr I just think NHS is doing it for budget cutting. Don\'t you think so Dr.? D: I am really sorry you felt this way. But NHS has planned those surgeries and set those criteria for delivering the best possible care to the patients. P: Alright',
        gender: 'female'
      },
      {
        id: 'rhinitis',
        name: 'Rhinitis',
        description: "You are an FY2 in GP. Luke, aged 25, has come with complaints of a runny nose for the past 2 days. Take history and address his concerns.",
        extraNotes: 'Allergic Rhinitis. Hx of allergy on skin. Family hx: Siblings have Eczema and Asthma',
        gender: 'male'
      }
    ]
  },
  {
    id: 'ophtha',
    name: 'Ophthalmology',
    icon: <AiOutlineEye />,
    topics: [
      {
        id: 'aacg',
        name: 'AACG',
        description: "Karishma aged, 50 came to the hospital with redness in her eye. Take history, assess the patient, and discuss the management with the patient.",
        extraNotes: 'Acute Angle-Closure Glaucoma (AACG). \'I have pain in my left eye.\' \'What you want to know.\' \'It started few hours ago.\' \'It is continuous and becoming worse.\' \'It is continuous and becoming worse.\' \'It was sudden.\' \'It is dull pain.\' \'I took PCM but didn\u2019t help.\' \'I took two tablets.\' \'It is 7 Dr.\' \'It is fine.\' \'I have headache in my left side of the head and eyebrow.\' \'Since my eye pain started.\' \'It is continuous.\' \'It was sudden.\' \'It is dull pain.\' \'It is 9.\' \'I have blurry vision, I can\u2019t see properly with my left eye.\' \'Yes, I feel nausea.\' \'Yes, I vomited two times.\' \'Yes. I have depression.\' \'6 months.\' \'I am taking Amitriptyline.\' PATIENT CONCERNS: \'1. Will Igo blind?\' \'2. Why do I have this problem?\'',
        gender: 'female'
      },
      {
        id: 'subconj-hemo',
        name: 'Subconj Hemo',
        description: "Luis, a 75-year-old, presents to A&E with a complaint of a red eye. You are the F2 doctor assigned to take his history and discuss management with him.",
        extraNotes: 'Subconjunctival Hemorrhage. complaint: \'my left eye suddenly became red\'. \'noticed at the mirror\'. PATIENT\'S CONCERNS: \'Will I go blind?\' \'How long before it goes back to normal?\'',
        gender: 'male'
      },
      {
        id: 'cataracts',
        name: 'Cataracts',
        description: "You are an F2 working in Medicine. Evelyn Addison, 65 years old, has some concerns. She went to her GP last week who advised her not to drive. Talk to her and address her concerns.",
        extraNotes: 'Cataracts. Complaint: \'I have some problem with my vision and my GP advised me not to drive.\' Patient\'s Concerns: \'Why did I have it?\' \'What\'s the treatment?\' \'Tell me about the surgery please?\' \'Dr It sounds very scary! They will operate in my eye without putting me on sleep! Please is there any other way that the surgeons can take care of my anxiety?\' \'Will they operate both eyes at the same time?\'',
        gender: 'female'
      },
      {
        id: 'armd',
        name: 'ARMD',
        description: "You are an F2 working in Medicine. Monica Ball, 85, has come with some visual problem. Talk to her and address her concerns.",
        extraNotes: 'ARMD. complaint: \'I see wavy lines instead of straight lines even with spectacles\'. No significant past medical hx. Concerns: \'will I go blind?\'',
        gender: 'female'
      },
      {
        id: 'opt-neuri',
        name: 'Opt Neuri',
        description: "You are FY2 in GP. A middle aged lady wants to see you. Talk to her, address her concerns and discuss further management.",
        extraNotes: 'OPTIC NEURITIS (MS). Complaints: \'I have pain in my eye from yesterday.\' \'I can\u2019t differentiate between colors as well.\' \'it happened 3 months ago as well but it resolved on it is own after a week.\' Every other eye symptoms are absent. \'Mom has MS\'.',
        gender: 'female'
      },
      {
        id: 'tun-vision',
        name: 'Tun Vision',
        description: "You are F2 in GP. Mr. Andy aged, 43 has been presented to the GP with visual problems. This is the patient's first visit. You don't know about the patient's past medical history. Please talk to the patient, assess the patient, examine his vision and discuss about the next step of management.",
        extraNotes: 'TUNNEL VISION/BITEMPORAL HEMIANOPIA/RIGHT HOMONYMOUS HEMIANOPIA/LEFT HOMONYMOUS HEMINANOPIA. D: What brought you to the hospital? P: I am fine, my wife thinks I have got some problem with my vision. D: Why your wife think like this? P: Because I scratched my eye D: Which side? P: Both side of my car;, D: When did it happen? P: It happened yesterday. D: How did it happen? P: I was parking my car and I scratched it. D: Has it happened before? P: Yes/No D: Do you usually bump into people or things? P: No D: Any car accidents? P: No D: Any headache? P: No D: Any blurry or double vision? P: No D: Do you see any colour halos? P: No D: Any nausea or vomiting?D: What\'s your job? P: I\'m a driver',
        gender: 'male'
      },
      {
        id: 'ret-det',
        name: 'Ret Det',
        description: "You are F2 in GP. Mr. Andy aged, 43 has been presented to the GP with visual problems. This is the patient's first visit. You don't know about the patient's past medical history. Please talk to the patient, assess the patient and discuss about the next step of management.",
        extraNotes: 'Retinal Detachment',
        gender: 'male'
      }
    ]
  },
  {
    id: 'dermatology',
    name: 'Dermatology',
    icon: <BiBody />, // Replace with appropriate icon from react-icons
    topics: [
      {
        id: 'mole-rmvl',
        name: 'Mole Rmvl',
        description: "You are F2 in Surgery Clinic. Mrs. Maria, aged 32, presented to the hospital due to the presence of a lesion on her right shoulder. She has a letter from her GP stating that the patient has concerns about the lesion and wants to remove it from her shoulder. Please talk to the patient, take focused history, assess your patient, discuss different options of management, and address her concerns. Discuss day case surgery and take relevant consent for the surgical procedure, if needed. Consent form is not available inside the cubicle.",
        extraNotes: 'Skin Lesion Mole. D: What brought you to the hospital? P: \'I have a lesion on my right shoulder. I want it to be removed. My GP referred me here.\' \'I had it from many years.\' \'Its about 1x1cm.\' \'round, brown.\' \'painful when it catches my clothes.\' D: Have you got any particular concern for the lesion to be removed now? P: \'I have my wedding coming up and my wedding dress won\'t cover it. It looks ugly. Also, sometimes it gets stuck in my dress and is quite uncomfortable.\' PATIENT CONCERNS: \'Dr. What is day case surgery?\' \'Will the surgery leave a scar?\' \'Will the lesion come back once it is removed?\' \'What if I decide to keep the lesion as it is without any treatment?\' \'How is the procedure done doctor?\' \'Shave excision? Is the surgery painful? Local anaesthesia? Why not put me to sleep? Pain/See/Hear? Complications?\' \'No\' \'Can I do it privately?\' \'How will they remove it Dr.?\'',
        gender: 'female'
      },
      {
        id: 'melanoma',
        name: 'Melanoma',
        description: "You are F2 in GP. Mrs. Maria, aged 39, presented to the clinic with a skin lesion on her shoulder. Please talk to the patient, take focused history, assess your patient, discuss different options of management, and address her concerns.",
        extraNotes: 'Skin Lesion Melanoma. Complaints: D: What brought you to the hospital? P: \'I have a lesion on my right shoulder. I want it to be removed. My GP referred me here.\' \'I had it from many years.\' \'increasing in size.\' \'Its about 2x3cm.\' \'irregular, mixed colours.\' D: Have you got any particular concern for the lesion to be removed now? P: \'I have my wedding coming up and my wedding dress won\'t cover it. It looks ugly.\'',
        gender: 'female'
      },
      {
        id: 'non-mlnm',
        name: 'Non-Mlnm',
        description: "You are F2 in GP. Mr. Donald, aged 60, has some concerns about his skin lesion/problem. Please talk to the patient, assess his condition, discuss your management, and address his concerns.",
        extraNotes: 'Skin Lesion Non-Melanoma (BCC, SCC). \'I have a lesion on top of my head. I want it to be checked as my wife is concerned about it. (patient shows left temporal or parietal area).\' \'2-3 months\' \'bleeds yes.\'',
        gender: 'male'
      },
      {
        id: 'scabies',
        name: 'Scabies',
        description: "You are an FY2 in GP. Parents of Sacha, aged 2, have come to you with some complaints. Sacha had gone for a pit walk with her father. Talk to the mother and address her concerns.",
        extraNotes: 'Scabies.',
        gender: 'female'
      },
      {
        id: 'syphilis',
        name: 'Syphilis',
        description: "You are an F2 working in GP. Stuart Broad, 24 years old, has come with a skin lesion on his private part. He is concerned about it. Talk to him, discuss management, and address his concerns.",
        extraNotes: 'Syphilis.',
        gender: 'male'
      },
      {
        id: 'gntl-warts',
        name: 'Gntl Warts',
        description: "You are an FY2 in GP. John, aged 25, has made an urgent appointment. Talk to him and address his concerns.",
        extraNotes: 'Genital Warts.',
        gender: 'male'
      },
      {
        id: 'labialis',
        name: 'Labialis',
        description: "You are an F2 in GP Clinic. A 24-year-old lady came with rashes on her lower lip. She is concerned about it. Please talk to the patient, take history, and address her concerns.",
        extraNotes: 'Herpes Labialis.',
        gender: 'female'
      },
      {
        id: 'urticaria',
        name: 'Urticaria',
        description: "You are an FY2 in GP. The mother of 5-year-old Daniel has some concerns. Talk to her and address her concerns.",
        extraNotes: 'Urticaria. Trigger: playing in the garden. Concerns: \'is it contagious?\' \'can he go to school?\'',
        gender: 'female'
      },
      {
        id: 'eczema',
        name: 'Eczema',
        description: "You are F2 in GP. John Smith, aged 15, came to the hospital with his mother with some concerns. He is a diagnosed case of Asthma. Please talk to them, take history, discuss your plan of management with them, and address their concerns.",
        extraNotes: 'Eczema.',
        gender: 'male'
      },
      {
        id: 'impetigo',
        name: 'Impetigo',
        description: "You are an F2 working in GP. Mary Stokes, 24 years old, has come with rashes on her face. She is concerned about it. Talk to her and address her concerns.",
        extraNotes: 'Impetigo.',
        gender: 'female'
      },
      {
        id: 'acne',
        name: 'Acne',
        description: "You are F2 in GP. Mariah, aged 24, came to the clinic. She is having Acne and wants Isotretinoin medications for it. Talk to the patient and discuss the plan of management with her.\n\n **NOTE:** Instructions paper is given in the cubicle. It states: Retinoid - Gel for mild to moderate acne treatment, and severe acne requires oral meds. Start as early as possible.",
        extraNotes: 'Mild Acne. Request for Roaccutane and insist because you saw it work for your friend. The doctor will not agree to prescribe but will need to refer you to dermatology. Taking COCP. Uses clearsil and face cleanser. washes face bid.',
        gender: 'female'
      },
      {
        id: 'tinea',
        name: 'Tinea',
        description: "You are an FY2 in GP. John Smith, aged 45, has come to you with some concerns. Talk to him and address his concerns.",
        extraNotes: 'Fungal Infection. Tinea Manuum.',
        gender: 'male'
      },
      {
        id: 'eczema-2',
        name: 'Eczema 2',
        description: "You are F2 in GP. John Smith, aged 15, came to the hospital with his mother with some concerns. He is a diagnosed case of Asthma. Please talk to them, take history, discuss your plan of management with them, and address their concerns.",
        extraNotes: 'Eczema.',
        gender: 'male'
      },
      {
        id: 'varicella',
        name: 'Varicella',
        description: "You are an FY2 in GP. Andy Charles, aged 4, was brought into the GP by his mother, Maggie Charles, because her son is not feeling well. Talk to her and address her concerns.",
        extraNotes: 'Concerned Mother - Chickenpox Child.',
        gender: 'female'
      },
      {
        id: 'sickleave',
        name: 'Sickleave',
        description: "You are an FY2 in GP. Mrs. Janet May, aged 26, has come to you with some concerns. Her daughter has chickenpox. Talk to her and negotiate with her.",
        extraNotes: 'Mother Wants Sick Note for herself (after daughter had Chickenpox). Request for sick leave. The doctor should not provide it. Daughter\'s illness has resolved. Daughter is stable.',
        gender: 'female'
      },
      {
        id: 'seb-kera',
        name: 'Seb Kera',
        description: "You are FY2 in GP. Nancy James, aged 70, emailed you a picture of a skin lesion. Talk to her and address her concerns.",
        extraNotes: 'Seborrheic Keratosis. lesion on chest. 2 months. increasing. mother has skin cancer. lives with wife. Concerns: \'is it cancer?\'',
        gender: 'female'
      }
    ]
  },
  {
    id: 'uro',
    name: 'Urology',
    icon: <BiHealth />,
    topics: [
      {
        id: 'hydrocoele',
        name: 'Hydrocoele',
        description: "You are an FY2 in GP, Dr. Daniel Finnish, aged 23, came to the GP surgery with testicular discomfort. Talk to him, assess him and address his concerns.",
        extraNotes: 'HYDROCELE. Complaint- \'I have discomfort in my testis\' \'feels like a heaviness\' \'right side\' \'constant\' \'1 week\'. Note- the doctor must examine the patient\'s scrotum to pass this station.',
        gender: 'male'
      },
      {
        id: 'orchitis',
        name: 'Orchitis',
        description: "You are FY2 in GP. Mr. Daniel White, aged 23, came to the GP surgery with testicular pain. Talk to him, assess him and address his concerns.",
        extraNotes: 'EPIDIDYMO-ORCHITIS. Pain, fever x 2days. Doesn\'t practice safe sex.',
        gender: 'male'
      },
      {
        id: 'sti-male',
        name: 'STI-male',
        description: "You are an FY2 in GP. Mr. Mark Jones, aged 23, has come to you with burning sensation whilst passing urine. Talk to him, assess him and address his concerns.",
        extraNotes: 'STI (MALE). Burning when I pee. Has a wife. Also has another sexual partner. If the doctor asks you to inform your sexual partners to come for testing, refuse to do so. Concerns: \'can I have sex?\'',
        gender: 'male'
      },
      {
        id: 'uti-trnsgdr',
        name: 'UTI-Trnsgdr',
        description: "You are an FY2 in GP. Miss Natalie Robbins, aged 18, has come to you with burning sensation whilst passing urine. She is under transition from female to male. Talk to her and address her concerns.",
        extraNotes: 'UTI IN FEMALE (TRANSITION FEMALE TO MALE). Burning pee, fever, tummy ache. Examination findings: Abdomen: Tenderness over suprapubic area. Urine dipstick- Nitrites, Leucocytes and micro-haematuria.',
        gender: 'female'
      },
      {
        id: 'uti-prgnt',
        name: 'UTI-Prgnt',
        description: "You are an FY2 in GP. Miss Samantha Truce, aged 30, has come to you with burning sensation whilst passing urine. She is 29 weeks pregnant. Talk to her and address her concerns.",
        extraNotes: 'UTI (PREGNANT). Urinalysis: Nitrates and leukocytes positive',
        gender: 'female'
      },
      {
        id: 'rcrnt-uti',
        name: 'Rcrnt UTI',
        description: "You are an F2 in GP Gen. Med. Sandra, aged 24, has a history of dysuria for 2 weeks. Her urine culture was negative. She was prescribed Trimethoprim and Nitrofurantoin but her dysuria persists. Talk to the patient, assess her and address her concerns.",
        extraNotes: 'RECURRENT UTI. Reveal that you are Non-compliant with meds if doctor probes deeper. Unprotected oral and vaginal sex. Concerns: \'Why do I have recurrent UTIs?\' \'I hope it won\'t happen again after this treatment\'',
        gender: 'female'
      },
      {
        id: 'lymph-hiv',
        name: 'Lymph HIV',
        description: "You are FY2 in General Practice. A 23‑year‑old male is coming with some complaints. Address them and discuss further management.",
        extraNotes: 'LYMPHADENOPATHY (HIV). STI. Recent travel to Thailand. Unprotected sex with multiple partners. Bisexual. Prefers receptive anal. Complaints: lumps and bumps at groin area. 2wks. no fever. no weight loss. no UTI symptoms. no rashes. Examination Findings: Generalized lymphadenopathy in whole body including axilla, groin and neck. Concerns: When doctor tells you the diagnosis of HIV, say \'are you sure?\'. \'how did I get it?\' \'any cure?\' \'so what can I do now?\' \'should I inform my employer?\'',
        gender: 'male'
      },
      {
        id: 'psa-test',
        name: 'PSA test',
        description: "You are an F2 in GP. Jason Roy, aged 55, came to the clinic requesting a PSA test. Please talk to the patient and address his concerns.",
        extraNotes: 'PSA Test Demanding Patient. Reason: his friend died of prostate cancer. No symptoms. Insist on doing the test even if doctor refuses. Concerns: ask about risks and benefits. precautions to take before the test.',
        gender: 'male'
      },
      {
        id: 'stones',
        name: 'Stones',
        description: "You are F2 in A&E. Mr. Patterson, aged 27 years, presented to the hospital with loin pain. The patient was given Diclofenac by the nurse. Please talk to the patient, take history, assess him and discuss the management with him.",
        extraNotes: 'Renal Stones. Loin Pain. Vomited once. smelly cloudy urine. Examination findings: EX: T-37, HR- 90, BP- 110/70, RR- 12-20, O2 sat- 96%. Tenderness in left flank area. Urine dip: Hematuria++. PATIENT\u2019S CONCERNS: P: What\u2019s happening doctor? P: What are you going to do now? P: Are stones a serious condition doctor? P: How did I get this stone?',
        gender: 'male'
      },
      {
        id: 'erec-dysf',
        name: 'Erec Dysf',
        description: "You are FY2 in GP. A middle-aged man wants to talk to you. Talk to him and address his concerns.",
        extraNotes: 'Erectile Dysfunction. Be hesitant and shy to say your complaint because you are embarrassed. but say it when the doctor reassures you. known HTN. using Labetalol. Concerns: request for cause and solutions.',
        gender: 'male'
      },
      {
        id: 'oxybutynin',
        name: 'Oxybutynin',
        description: "You are FY2 in General Practice. A 50‑year‑old lady is coming with some concerns. Talk to her and address her concerns.",
        extraNotes: 'Transient Confusion and memory loss. Side effect of Oxybutynin started recently. Positive Urinary Incontinence.',
        gender: 'female'
      },
      {
        id: 'urge-inco',
        name: 'Urge Inco.',
        description: "You are FY2 in General Practice. A 50‑year‑old lady is coming with some concerns. Talk to her and address her concerns.",
        extraNotes: 'Overactive bladder. Urge incontinence.',
        gender: 'female'
      },
      {
        id: 'bph-prca',
        name: 'BPH/PrCa',
        description: "You are F2 in GP. Mr. Zimmerman, aged 57, presented to the clinic with haematuria. Please take history, assess him and discuss the management plan with him.",
        extraNotes: 'Haematuria. BPH or PrCa. +ve LUTs. No weight loss. EX: prostate is slightly enlarged and is smooth in surface and consistency. In urine dip +++ hematuria. React with emotions when the doctor mentions possibility of prostate cancer.',
        gender: 'male'
      }
    ]
  },
  {
    id: 'psychia',
    name: 'Psychiatry',
    icon: <TbMoodHappy />,
    topics: [
      {
        id: 'psychotic',
        name: 'Psychotic',
        description: "You are F2 in A&E. Gordon, aged 29, has been brought to the hospital by the police. He thinks he has been followed by the police, so he went to the police to give himself up. Police investigated him and confirmed that he has not done anything wrong and there is no evidence of his claim. He works in the post office. Please talk to the patient, take history, explain the diagnosis to the patient, and discuss your initial plan of management with him.",
        extraNotes: 'Psychotic Patient. Note: Sometimes he looks anxious. Sometimes he shows his anxiousness and agitation by moving his hands while talking to you. Sometimes this patient doesn\u2019t give good eye contact. Sometimes he keeps looking at the door or on the floor.',
        gender: 'male'
      },
      {
        id: 'alc-dep',
        name: 'Alc Dep',
        description: "You are F2 Surgery. Mrs. Samantha Gray, aged 50, has undergone a Hysteroscopy. The result of the test is normal. She is about to be discharged. Please talk about her alcohol consumption and discuss the management with the patient. The nurse has some concerns about her alcohol intake and asked you to talk to her regarding her alcohol habits.",
        extraNotes: 'Alcohol Dependency. Your first message should be to request for the result of your test. Don\'t discuss your alcohol issue unless the doctor asks you. Even when the doctor asks you, deny it at first before you agree. Patient tried stopping before but had withdrawal symptoms.',
        gender: 'female'
      },
      {
        id: 'drug-dep',
        name: 'Drug Dep',
        description: "You are F2 in Psychiatry. Mr. Jack Sparrow, aged 35, has been referred to you. He wants to quit taking drugs. Please talk to the patient, take history, talk about his opioid abuse, and discuss management options with him.",
        extraNotes: 'Drug Dependency. Heroin. Complaint: \'Doctor. I am taking drugs and I am addicted to it. I want to give it up! I went to my GP and he sent me to the hospital.\' Patient sniffs it before but now injects. He knows about the needle exchange programme. Takes other drugs like Cannabis, Amphetamine and Cocaine. Tried stopping but had withdrawal symptoms. Not working at the moment. Has had trouble with the law.',
        gender: 'male'
      },
      {
        id: 'anor-nerv',
        name: 'Anor Nerv',
        description: "You are F2 in Psychiatry. Emma, aged 18, is sent to the hospital by the GP because she was losing weight for the past 6 months. She has not been diagnosed with any medical condition. She is not taking any medication. Her BMI is 17. Talk to the patient and discuss the initial plan of management with her.",
        extraNotes: 'Anorexia Nervosa. Complaint: \'My parents have some concern about my weight loss. They took me to GP and he sent me to the hospital.\' Be in denial when doctor explains the diagnosis.',
        gender: 'female'
      },
      {
        id: 'dprssn-cbt',
        name: 'Dprssn CBT',
        description: "You are FY2 in GP clinic. Steven Douglas, aged 35, has been divorced from his wife and is in depression. He saw the psychiatrist, was given CBT treatment, but he is not improving on CBT. Talk to the patient and address his concerns.",
        extraNotes: 'Depression with Failed CBT. Has had 6 sessions of CBT. Complaint: \'Doctor I have been on treatment but still feel sad.\' Divorced the wife 2 months ago. Wants to stop CBT. If doctor suggests antidepressants, show concerns about side effects, addiction, duration. Vary your emotions.',
        gender: 'male'
      },
      {
        id: 'dprssn',
        name: 'Dprssn',
        description: "You are an FY2 in GP. Diana Whales, aged 30, came to the clinic because of concern of weight loss. Please talk to her and discuss your plan of management with her and address her concerns.",
        extraNotes: 'Depression. Complaint: weight loss x 1yr.',
        gender: 'female'
      },
      {
        id: 'alc-colg',
        name: 'Alc Colg',
        description: "You are an F2 who has just started your Surgery Rotation. Your colleague Peter Roocroft, aged 23, is also an F2 who has started the surgery rotation. You saw him on the ward round last week, and he smelt of alcohol. You've noticed it again this morning, and a few others have also noticed. You have arranged to meet with him in a quiet room after finishing your work. Talk to your colleague about his problem and assess his situation.",
        extraNotes: 'Alcohol Dependency. If doctor confronts you directly about alcohol show anger and denial. But if doctor goes indirectly and asks about your work life, social life, home life, gradually open up to him. Mention a significant life event that happened prior to the onset eg breaking up with girlfriend etc. Ask the doctor not to inform anybody and to keep it a secret.',
        gender: 'male'
      },
      {
        id: 'coc-colg',
        name: 'Coc Colg',
        description: "You are an F2 in General Medicine. John Figo, 25 years old, is a final year medical student. You saw him at a birthday party last night where he was drinking heavily and taking cocaine. The following morning, you overheard nurse colleagues discussing his agitated and hyper-excited behavior during the ward round. Talk to your colleague, assess his condition, and manage the situation appropriately.",
        extraNotes: 'Colleague with Substance Abuse. If doctor confronts you directly about partying and cocaine use show anger and denial. Also try to deny and downplay the party. But if doctor goes indirectly and asks about your work life, social life, home life, gradually open up to him. Mention a significant life event that happened prior to the onset eg breaking up with girlfriend etc. Ask the doctor not to inform anybody and to keep it a secret.',
        gender: 'male'
      },
      {
        id: 'late-colg',
        name: 'Late Colg',
        description: "You are an F2 in Medicine. Darren, a final-year student who is part of your medical team, has been consistently arriving late to the ward. Your colleague has also noticed this. Please talk to him.",
        extraNotes: 'Professionalism Issue - Colleague coming late. Be non-challant, confrontational. Try to downplay the issue. Your home is 2hrs away by train. Much later you will become apologetic.',
        gender: 'male'
      },
      {
        id: 'soc-media',
        name: 'Soc Media',
        description: "You are an F2 working in A&E. Your colleague Peter, an FY1 doctor, made a post on Facebook about an elderly lady in the emergency department with confusion, who believed herself to be the Queen of England. Please talk to him and address the confidentiality issue.",
        extraNotes: 'Colleague Confidentiality Breach - Facebook Post. After pleasantries, if your colleague asks about your recent social media post say, \'Oh yes doctor. You know I could not stop laughing while seeing the patient. Her name was Diana. She was confused and she was thinking herself to be the Queen of England. Hilarious. She was making funny comments like she lost her crowns and she was looking for it. So, I made a video of her and posted it.\' If doctor mentions that ethical and confidentiality rules were broken say \'But I didn\'t mean to break the rules. It was just for fun.\' If doctor validates your feelings say \'To be honest I didn\'t think that much before posting it.\' If doctor tells you about GMC and/or legal action say \'Yes, you are right. I am so sorry for what I have done. What should I do now?\' If doctor tells you to delete the post agree to do so. Also agree to apologise to the patient who is now conscious.',
        gender: 'male'
      }
    ]
  },
  {
    id: 'counsel',
    name: 'Counsel',
    icon: <BiChat />,
    topics: [
      {
        id: 'preconception-counselling',
        name: 'PreConception Counselling - Cystic Fibrosis',
        description: 'You are FY2 in GP Clinic. Lowe Austin came with some concerns. Talk to her and address her concerns.',
        extraNotes: 'Patient expresses concerns about cystic fibrosis in her family, including worries about her future child potentially having the disease and questions about genetic testing.',
        gender: 'female'
      },
      {
        id: 'post-op-hemiarthroplasty',
        name: 'Post-op Hemiarthroplasty',
        description: 'You are an F2 in Orthopaedics. Mrs. Bond, aged 78, presented to the hospital due to a fracture of the neck of femur. She has been planned to undergo a hemiarthroplasty. Please talk to the patient, discuss post-operative management and complications, and discuss the management plan after discharge. Address her concerns.',
        extraNotes: 'Patient concerns include understanding the post-op recovery process, potential complications like blood clots, and managing mobility post-surgery.',
        gender: 'female'
      },
      {
        id: 'pre-op-ankle-pin-removal',
        name: 'Pre-op Assessment for Ankle Pin Removal',
        description: 'You are an F2 in Surgery. Mr. Peter Murphy, aged 48, has come to the hospital for his pre-operative assessment. He is scheduled for an operation under general anaesthesia for screw removal in his ankle in two weeks. Please talk to the patient, assess his pre-op fitness for day care surgery, and address his concerns.',
        extraNotes: 'Patient is diabetic with a history of ankle surgery and needs pre-op assessment for day surgery.',
        gender: 'male'
      },
      {
        id: 'pre-op-cholecystectomy',
        name: 'Pre-operative Care for Cholecystectomy',
        description: 'You are an F2 in Surgery. Ms. Sally Armstrong, aged 25, has come for her pre-operative assessment. Please talk to her, check her fitness for surgery, and take verbal consent for the procedure.',
        extraNotes: 'Patient concerns include surgical incision size, blood loss, and Jehovah\u2019s Witness religious views regarding blood transfusions.',
        gender: 'female'
      },
      {
        id: 'pre-op-herniorrhaphy',
        name: 'Pre-operative Herniorrhaphy',
        description: 'You are an F2 in Surgery. Mr. James Anderson, aged 40, has been planned for a right inguinal hernia repair. The anaesthetist has completed the assessment, and his blood pressure is 152/88 mmHg. Please explain the procedure to him and address any concerns he may have. The consultant will come later to take formal consent.',
        extraNotes: 'Patient is concerned about the surgery procedure, post-operative care, and resuming normal activities such as work and sex.',
        gender: 'male'
      },
      {
        id: 'dermoid-cyst',
        name: 'Dermoid Cyst Surgery',
        description: 'You are an F2 in Surgery. Yulia, aged 24, presents with abdominal pain. An ultrasound shows a dermoid cyst in the right ovary, and the consultant has decided to perform an open ovarian cystectomy with an 8.5 cm incision. The consultant has also decided to keep her in the hospital for 2 days post-surgery. Please talk to the patient and address her concerns.',
        extraNotes: 'Patient concerns include the seriousness of the cyst, surgery details, scar size, and potential impact on fertility.',
        gender: 'female'
      },
      {
        id: 'breast-cancer-pain',
        name: 'Breast Cancer with Back Pain',
        description: 'You are an F2 in the Acute Medicine Unit. Jasmine, aged 60, has been referred from the Oncology Department for pain management. She was diagnosed with breast cancer 5 years ago and now presents with back pain. Please talk to the patient, outline a treatment plan, and address her concerns.',
        extraNotes: 'Patient expresses concerns about morphine addiction, pain management options, and attending a family wedding despite ongoing pain.',
        gender: 'female'
      },
      {
        id: 'prostate-cancer-pain',
        name: 'Prostate Cancer with Back Pain',
        description: 'You are an F2 in the Medical Unit. Michael, aged 60, has been referred from the Oncology Department for pain management. He was diagnosed with prostate cancer 5 years ago and now presents with back pain. Please talk to the patient, outline a treatment plan, and address his concerns.',
        extraNotes: 'Similar to breast cancer patient, this patient is concerned about pain management, morphine addiction, and alternative routes for medication.',
        gender: 'male'
      },
      {
        id: 'colorectal-polyp',
        name: 'Colorectal Polyp',
        description: 'You are an FY2 in Gastroenterology. The patient, after undergoing a sigmoidoscopy, is concerned about having to repeat the test. Explain the reason and provide reassurance.',
        extraNotes: 'Patient has concerns about the need for a repeat camera test after the previous one did not provide immediate results.',
        gender: 'male'
      }
    ]
  },
  {
    id: 'followup',
    name: 'Followup',
    icon: <BiCalendarCheck />,
    topics: [
      {
        id: 'ph-neura',
        name: 'PH Neura',
        description: "You are FY2 in GP. Benjamin White, aged 72, has come for consultation. He was diagnosed with Shingles 2 months back and was given Acyclovir. He saw his GP 1 month back for the pain on the right side of his chest and was given Paracetamol and Codeine. Talk to him and address his concerns.",
        extraNotes: 'POST HERPETIC NEURALGIA on followup. Still has pain:',
        gender: 'male'
      },
      {
        id: 'pmr-flwup',
        name: 'PMR FlwUp',
        description: "You are an FY2 in GP. Mrs. Janet James, aged 50, has been diagnosed with polymyalgia rheumatica. She is on steroids, aspirin, lansoprazole and bisphosphonates. ESR and CRP are normal. Talk to her and address her concerns.",
        extraNotes: 'POLYMYALGIA RHEUMATICA (PMR) REFUSING STEROIDS (due to Side effects of steroids). \'I don\'t want to take steroids anymore\'. have been on steroids for 1 year. gained 2 stones weight. developed hypertension and diabetes. pains has stopped. on bisphosphonates. vital signs: Weight: 13 stones (82.5 kg). Height: 171 cm.',
        gender: 'female'
      },
      {
        id: 'strd-rvw',
        name: 'Strd Rvw',
        description: "Jackson Avery, 40 years old, has called you to talk about certain concerns. He is a known case of polymyalgia rheumatica and is currently on lansoprazole, bisphosphonates, and steroids. Talk to him and address his concerns.",
        extraNotes: 'Steroid Review. Polymyalgia Rheumatica. Patient is enquiring about steroid side effects. First sentence \'Doctor, I have certain concerns about the medications that I am taking\'. Much later if doctor probes say: \'I want to know the side effects of steroid.\' concerns after doctor has told you the side effects: \'Doctor why should I take this medication since there are so many side effects?\'',
        gender: 'male'
      },
      {
        id: 'barretts',
        name: "Barrett's",
        description: "You are F2 in Surgery. Louis, aged 40, had an endoscopy and biopsy, and he is here for his reports. The diagnosis of Barrett’s oesophagus has been made. It is columnar metaplasia, which is pre-malignant, non-invasive, and non-metastatic. He was advised to repeat the endoscopy after 3 years. Talk to the patient, take history, explain the diagnosis, discuss the management, and address his concerns. You can find the reports in the cubicle.",
        extraNotes: 'Barrett\'s Oesophagitis. c/o heartburn 6months',
        gender: 'male'
      },
      {
        id: 'thalasemia',
        name: 'Thalasemia',
        description: "You are FY2 in the GP clinic. Mrs. Mary, aged 45, has come for a well-woman checkup. Her blood test results from three weeks ago are as follows: Hb: 110 g/dl (115-165), TLC: 4000/cmm, PLT: 430,000, MCV: 58 (80-100), U&E: Normal, LFTs: Normal, Serum Iron: Normal, Serum Ferritin: Normal. Discuss these test results with Mrs. Mary, take an appropriate history, and discuss the management with her.",
        extraNotes: 'Anaemia 2 Thalasemia. Nil complaints/symptoms. for checkup then blood tests were done. now came for test results followup. family hx of thalasemia in sister.',
        gender: 'female'
      },
      {
        id: 'myeloma',
        name: 'Myeloma',
        description: "You are an FY2 in GP. Mrs. Walker, aged 52, has come in for her blood results. Results: Hb: 100, MCV: Normal, RA factor: Normal, IgG: Raised, Urine: Bence Jones Protein Positive. Talk to her and address her concerns.",
        extraNotes: 'Multiple Myeloma. came for test results. have backache (lower back) & fatigue. PATIENT\'S CONCERNS: \'1. Is it rheumatoid arthritis?\' \'2. Is it cancer?\' \'3. What are you going to do for me?\'',
        gender: 'female'
      }
    ]
  },
  {
    id: 'teaching',
    name: 'Teaching',
    icon: <GiTeacher />,
    topics: [
      {
        id: 'ecg',
        name: 'ECG',
        description: "You are an F2 in A&E. Alexa, your nurse colleague, has come to you to learn about ECG. Talk to her about ECG.",
        extraNotes: 'Note: Procedure station',
        gender: 'female'
      },
      {
        id: 'viith-cn',
        name: 'VIIth CN',
        description: "You are an F2 working in neurology. David, your colleague, wants to learn how to perform a VIIth cranial nerve examination. Talk to him and teach him.",
        extraNotes: 'Teaching Station: VIIth Cranial Nerve Examination.',
        gender: 'male'
      },
      {
        id: 'viii-th-cn',
        name: 'VIIIth CN',
        description: "You are an F2 working in neurology. David, your colleague, wants to learn how to perform a VIIIth cranial nerve examination. Talk to him and teach him.",
        extraNotes: 'Teaching Station-VIIIth Cranial Nerve Examination.',
        gender: 'male'
      },
      {
        id: 'knee-exam',
        name: 'Knee Exam',
        description: "You are F2 in Orthopaedics. Tom came to the hospital with a knee problem. Your colleague David has taken the history but doesn't know how to perform a knee examination. Please teach him how to conduct the knee examination without allowing him to do it.",
        extraNotes: 'Teaching Station-Knee Examination.',
        gender: 'male'
      },
      {
        id: 'bls-adult',
        name: 'BLS-Adult',
        description: "You are F2 in a general medical ward. Alex, an FY1 doctor, has not attended his BLS workshop. Please demonstrate and teach him the steps of BLS. Explain the steps, assess his understanding, and provide feedback.",
        extraNotes: 'Teaching Station-Basic Life Support (BLS) - Adult.',
        gender: 'male'
      },
      {
        id: 'bls-pedo',
        name: 'BLS-Pedo',
        description: "You are an F2 working in Pediatrics. Alex, a final year medical student, wants to learn how to perform Pediatric BLS. Teach Alex how to perform BLS on a 6-year-old child.",
        extraNotes: 'Teaching Station-Pediatric Basic Life Support (BLS).',
        gender: 'male'
      },
      {
        id: 'epipen',
        name: 'Epipen',
        description: "You are an FY2 in the GP Surgery. Jason Winslow, an 8-year-old boy, was admitted to the hospital with anaphylaxis after ingesting peanuts one week ago. His mother, Becca Winslow, has questions about how to use the EpiPen. (There is a dummy EpiPen on the table.) Teach her how to use the EpiPen and address her concerns.",
        extraNotes: 'Teaching Station-EpiPen Teaching.',
        gender: 'female'
      },
      {
        id: 'sc-inj',
        name: 'SC Inj',
        description: "You are FY2 in the skills lab. Lorrie, a 3rd-year medical student, is here. Teach him how to administer a subcutaneous injection without asking him to perform the procedure.",
        extraNotes: 'Teaching Station-Subcutaneous Injection Teaching.',
        gender: 'male'
      },
      {
        id: 'urinalysis',
        name: 'Urinalysis',
        description: "You are FY2 in the skills lab. Lorrie, a 3rd-year medical student, is here. Teach him how to do a urinalysis test without asking him to perform the procedure.",
        extraNotes: 'Teaching Station-Urinalysis testing Teaching.',
        gender: 'male'
      }
    ]
  },
  {
    id: 'procedures',
    name: 'Procedures',
    icon: <GiSyringe />,
    topics: [
      {
        id: 'pcm-od',
        name: 'PCM O.D',
        description: "You are F2 in A&E. Mrs. Billie Brown, aged 25, has taken some paracetamol tablets. Perform venepuncture for necessary investigations, talk to the patient, take relevant history, discuss further management with her, and address her concerns.",
        extraNotes: 'This station is for procedures. The doctor must verbally run-through the steps for the procedure in order to pass. Paracetamol Overdose. 40 tablets. 6hrs ago. You should give the PCM blood test result level when candidate mentions it: \'Blood PCM Level; 81 = above treatment line\'',
        gender: 'female'
      },
      {
        id: 'iv-cannula',
        name: 'IV Cannula',
        description: "You are F2 in surgery. Mr. Peter Smith, aged 27, was diagnosed with a ruptured appendix and underwent surgery a few hours ago. His IV cannula is now blocked. Please talk to the patient and change the IV cannula. After 6 minutes, the examiner will ask you some questions.",
        extraNotes: 'This station is for procedures. The doctor must verbally run-through the steps for the procedure in order to pass. IV Cannulation. c/o \'Doctor I am in pain. I had an operation for my appendix, give me some pain killer\'. also vomiting. if they are asking too many questions (apart from PMHx, Medication Hx & Allergy Hx), refuse to answer until they give you the IV paracetamol and Metoclopramide. wince in pain if they don\'t warn you about \'sharp scratch coming\'. EXAMINER: Provide Chart when candidate requests: 5 mg IV Morphine every 4 hours. Last dose received 1 hour ago. Vitals: RR: 12-20/min. Temperature: 38, PR: 94, O2 sat: 92% to 94%, BP: 120/80. Examiner asks: 1. what are you going to do for the patient? after candidate answers, 2. what if pain continues?',
        gender: 'male'
      },
      {
        id: 'catheter',
        name: 'Catheter',
        description: "You are F2 in A&E. George Jefferson, aged 45, comes to you complaining of lower abdominal discomfort. No one has seen the patient earlier. Please talk to the patient, take relevant history, assess him, and perform emergency management. Discuss further management with the patient and carry out the catheterization procedure if needed.",
        extraNotes: 'This station is for procedures. The doctor must verbally run-through the steps for the procedure in order to pass. Urethral Catheterization.',
        gender: 'male'
      },
      {
        id: 'blood-draw', name: 'Blood Draw',
        description: 'Demonstrate safe venepuncture technique. Explain procedure to nervous patient.',
        extraNotes: 'Note: Procedure station',
        gender: 'male'
      },
      {
        id: 'ecg', name: 'ECG Recording',
        description: 'Perform 12-lead ECG. Demonstrate correct lead placement and technique.',
        extraNotes: 'Note: Procedure station',
        gender: 'male'
      },
    ]
  },
];

interface SpecialtySelectorProps {
  onTopicSelect: (specialty: Specialty, topic: Topic) => void;
}

// Create a custom hook to handle the back functionality
export function useSpecialtyNavigation() {
  const [selectedSpecialty, setSelectedSpecialty] = useState<Specialty | null>(null);
  const [selectedTopic, setSelectedTopic] = useState<Topic | null>(null);
  const [showSelector, setShowSelector] = useState(true);

  const handleBack = () => {
    if (!showSelector) return; // Prevent going back during active session
    if (selectedTopic) {
      setSelectedTopic(null);
    } else {
      setSelectedSpecialty(null);
    }
  };

  return {
    selectedSpecialty,
    setSelectedSpecialty,
    selectedTopic,
    setSelectedTopic,
    showSelector,
    setShowSelector,
    handleBack
  };
}

export function SpecialtySelector({ onTopicSelect }: SpecialtySelectorProps) {
  const {
    selectedSpecialty,
    setSelectedSpecialty,
    selectedTopic,
    setSelectedTopic,
    showSelector,
    setShowSelector,
    handleBack
  } = useSpecialtyNavigation();

  const [isExpanded, setIsExpanded] = useState(true);
  const [showSessionSummary, setShowSessionSummary] = useState(false);
  const [guideMessage, setGuideMessage] = useState<string>('');
  const [sessionCompleted, setSessionCompleted] = useState(false);
  const [completedSpecialty, setCompletedSpecialty] = useState<Specialty | null>(null);
  const [completedTopic, setCompletedTopic] = useState<Topic | null>(null);
  const [completedTopics, setCompletedTopics] = useState<string[]>(() => {
    const storedTopics = localStorage.getItem('completedTopics');
    return storedTopics ? JSON.parse(storedTopics) : [];
  });
  const { client, connect } = useLiveAPIContext();
  const [showUpgradePopup, setShowUpgradePopup] = useState(false);
  const { user } = useAuth();
  const soundEngine = useRef<CallSoundEngine | null>(null);
  const [isRinging, setIsRinging] = useState(false);
  const [isExpired, setIsExpired] = useState(false);

  // New state for session history and flow
  const [sessionHistory, setSessionHistory] = useState<SessionSummaryRecord[]>([]);
  const [isPreparingResults, setIsPreparingResults] = useState(false);

  // Sync session history
  useEffect(() => {
    const loadHistory = async () => {
      // 1. Get local history
      const localHistory = JSON.parse(localStorage.getItem('sessionSummaries') || '[]');

      if (user) {
        // 2. Get remote history if logged in
        const remoteHistory = await getSessionHistory(user.id);

        // 3. Merge strategies (simple: remote takes precedence for logged in user, 
        // ensuring we have their comprehensive history)
        // We could also merge local-only sessions if they were played while offline, 
        // but for now, let's prioritize the cloud source of truth + local cache update.
        if (remoteHistory && remoteHistory.length > 0) {
          setSessionHistory(remoteHistory);
          localStorage.setItem('sessionSummaries', JSON.stringify(remoteHistory));
        } else {
          // Keep local if remote fails or is empty (and we have local data?)
          // If remote is empty but we have local, it might mean they are new to this device 
          // or new account. Let's trust local for now if remote is empty.
          if (localHistory.length > 0) setSessionHistory(localHistory);
        }
      } else {
        // Anonymous: use local storage
        setSessionHistory(localHistory);
      }
    };

    loadHistory();
  }, [user]);

  // Helper to get grade for a topic
  const getTopicGrade = (topicId: string) => {
    // Find all attempts for this topic (normalizing names if needed, but IDs are better. 
    // The current history saves 'topicName' and 'specialtyName'. 
    // Ideally we should have saved IDs, but we work with names as per existing save logic.)

    // We need to find the topic object to get its name for comparison
    // But here we are rendering the topic, so we have the topic object.

    // Filter history for this topic
    const attempts = sessionHistory.filter(h =>
      // Match by name since ID wasn't saved in the SessionSummary interface previously
      h.topic_name === topicId || h.topic_name === getTopicNameById(topicId)
    );

    if (attempts.length === 0) return null;

    // Sort by latest (though getSessionHistory already sorts by created_at desc)
    // Actually, we might want the *best* score or the *latest*? 
    // "Display last grade achieved" -> Latest.
    const lastAttempt = attempts[0];

    // Calculate total score
    const total = (lastAttempt.ips_score || 0) + (lastAttempt.data_gathering_score || 0) + (lastAttempt.diagnosis_mgt_score || 0);
    const passed = total >= 9;

    return { score: total, passed };
  };

  const getTopicNameById = (id: string) => {
    for (const s of SPECIALTIES) {
      const t = s.topics.find(t => t.id === id);
      if (t) return t.name;
    }
    return id;
  };

  const getSpecialtyProgress = (specialty: Specialty) => {
    if (specialty.topics.length === 0) return { percent: 0, completed: false };

    const passedCount = specialty.topics.reduce((acc, topic) => {
      const grade = getTopicGrade(topic.name);
      return acc + (grade?.passed ? 1 : 0);
    }, 0);

    const percent = Math.round((passedCount / specialty.topics.length) * 100);
    return { percent, completed: percent === 100 };
  };

  const [showRegistrationPrompt, setShowRegistrationPrompt] = useState(false);
  const [remainingCases, setRemainingCases] = useState(DAILY_FREE_LIMIT);
  const navigate = useNavigate();
  const firstTimeUser = isFirstTimeUser();
  const [showOnboarding, setShowOnboarding] = useState(firstTimeUser && !isOnboardingCompleted());
  const timeoutRef = useRef<NodeJS.Timeout>();
  const summaryShownRef = useRef(false); // new

  // Keep screen awake during active session
  useWakeLock(!!selectedTopic);

  useEffect(() => {
    if (firstTimeUser && !isOnboardingCompleted()) {
      setShowOnboarding(true);
    }
  }, [firstTimeUser]);

  useEffect(() => {
    const checkCaseLimit = async () => {
      if (!user) {
        try {
          const remaining = await getRemainingCases();
          setRemainingCases(remaining);
          if (remaining === 0) {
            setShowRegistrationPrompt(true);
          }
        } catch (error) {
          console.error('Error checking case limit:', error);
          setRemainingCases(0); // Default to 0 in case of error
        }
      }
    };
    checkCaseLimit();
  }, [user]);



  useEffect(() => {
    soundEngine.current = new CallSoundEngine();
    return () => {
      soundEngine.current?.stopAll();
      soundEngine.current = null;
    };
  }, []);



  useEffect(() => {
    return onNavigationBack(() => {
      if (selectedTopic) {
        setSelectedTopic(null);
      } else {
        setSelectedSpecialty(null);
      }
      setShowSelector(true);
    });
  }, [selectedTopic, setSelectedTopic, setSelectedSpecialty, setShowSelector]);

  const handleTopicSelect = async (specialty: Specialty, topic: Topic) => {
    // Get current remaining cases
    const currentRemainingCases = await getRemainingCases();

    // Check subscription status first

    if (user?.email) {
      const status = await subscriptionService.checkSubscriptionStatus(user.email);
      if (status.status === 'expired') {
        setIsExpired(true);
        setShowUpgradePopup(true);
        return;
      }
    }

    if (!user && currentRemainingCases === 0) {
      setShowRegistrationPrompt(true);
      return;
    }

    if (!user) {
      await trackCaseUsage();
      try {
        const remaining = await getRemainingCases();
        setRemainingCases(remaining);
      } catch (error) {
        console.error('Error getting remaining cases:', error);
      }
    }

    // Start ringing when connection attempt begins
    try {
      // Start professional ringtone
      soundEngine.current?.stopAll();
      await soundEngine.current?.startRingtone();
      setIsRinging(true);
    } catch (error) {
      console.error('Error playing ringtone:', error);
    }
    // Check if user is on free tier
    const isPremiumViaAuth = user?.user_metadata?.subscription_status === 'premium';
    const premiumUsersList = await subscriptionService.getAllPremiumUsers();
    const isPremiumViaAdmin = premiumUsersList.some(premiumUser => premiumUser.email === user?.email);

    if (!isPremiumViaAuth && !isPremiumViaAdmin) {
      const limitExceeded = await trackCaseUsage();
      if (limitExceeded) {
        setShowUpgradePopup(true);
        soundEngine.current?.stopAll(); // Add cleanup here
        return;
      }
    }

    if (specialty.id === 'random') {
      // Get all specialties except the random one
      const availableSpecialties = SPECIALTIES.filter(s => s.id !== 'random');
      // Pick a random specialty
      const randomSpecialty = availableSpecialties[Math.floor(Math.random() * availableSpecialties.length)];
      // Pick a random topic from that specialty
      const randomTopic = randomSpecialty.topics[Math.floor(Math.random() * randomSpecialty.topics.length)];

      // Set the selected specialty first (this will show the topics list)
      setSelectedSpecialty(randomSpecialty);

      // Small delay to allow state update
      await new Promise(resolve => setTimeout(resolve, 100));

      // Then proceed with topic selection using the random specialty and topic
      handleTopicSelect(randomSpecialty, randomTopic);
      return;
    }

    // Rest of the existing handleTopicSelect function
    const context = {
      specialty: specialty.name,
      topic: topic.name,
      description: topic.description,
      gender: topic.gender || ('male' as const),
    };

    setSelectedTopic(topic);
    setShowSelector(false);

    // Add longer delay before connection attempt
    await new Promise(resolve => setTimeout(resolve, 1000));

    let retryCount = 0;
    const maxRetries = 3;

    while (retryCount < maxRetries) {
      try {
        await connect(context);




        // Add delay after connection to ensure WebSocket is ready
        await new Promise(resolve => setTimeout(resolve, 1500));

        if (!client?.ws) {
          throw new Error("WebSocket not initialized");
        }

        // Check WebSocket readyState
        if (client.ws.readyState !== WebSocket.OPEN) {
          throw new Error("WebSocket not in OPEN state");
        }

        // Send initialization message
        await client.send([{
          text: `Speak the START PHRASE.

This is your Scenario: ${topic.description}
Further instructions + diagnosis to simulate: ${topic.extraNotes}
Remember: act as the patient. I'm the doctor.
                `
        }]);

        // STOP RINGING ON SUCCESSFUL CONNECTION
        soundEngine.current?.stopAll();
        setIsRinging(false);

        onTopicSelect(specialty, topic);
        break; // Success - exit the retry loop


      } catch (error) {
        console.error(`Connection attempt ${retryCount + 1} failed:`, error);
        soundEngine.current?.stopAll();
        setIsRinging(false);
        retryCount++;

        if (retryCount === maxRetries) {
          // usual failure logic
          console.error('Failed to start session after all retries');
          setShowSelector(true);
          setSelectedTopic(null);
          setSelectedSpecialty(null);
          alert('Failed to connect to the session. Please try again.');
          return;
        }

        // Longer wait between retries
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    }
  };



  const handleTimeUp = () => {
    if (!user) {
      navigate('/intermediate');
      return;
    }

    // Reset state and go back to selector — the automated summary
    // via submit_session_summary handles the popup via App.tsx

    // FIX: If session summary is already visible (e.g. user manually clicked finish), don't show "Preparing Results"
    // Also check ref to be doubly sure
    if (showSessionSummary || summaryShownRef.current) {
      setSelectedSpecialty(null);
      setSelectedTopic(null);
      setShowSelector(true);
      setSessionCompleted(false);
      setGuideMessage('');
      setCompletedSpecialty(null);
      setCompletedTopic(null);
      summaryShownRef.current = false;
      setIsPreparingResults(false); // Ensure this is off
      return;
    }

    // Show "Preparing Results" modal/overlay
    setIsPreparingResults(true);

    // We do NOT reset selectedTopic yet, so the "Active Scenario" view is technically still "active" 
    // but covered by the loader, OR we switch to a loading state.
    // If we set selectedTopic(null), it goes to Homescreen.
    // So let's set selectedTopic(null) BUT keeps a flag that we are "transitioning".

    setTimeout(() => {
      setSelectedSpecialty(null);
      setSelectedTopic(null);
      setShowSelector(false); // Hide selector to prevent homescreen flash
      // isPreparingResults remains true until the global session summary popup appears 
      // OR we time it out.
      // Actually, App.tsx triggers `setShowSessionSummary(true)` based on `handleCaseComplete`.
      // We need to ensure we don't get stuck.

      setSessionCompleted(false);
      setGuideMessage('');
      setCompletedSpecialty(null);
      setCompletedTopic(null);
      summaryShownRef.current = false;
    }, 100);
  };

  // Listen for session summary close to reset loading state
  useEffect(() => {
    // If we are in "Preparing" state and the summary popup opens (handled in App.tsx),
    // we can reset our internal loading state. 
    // However, SpecialtySelector doesn't know when App.tsx opens the popup unless we pass a prop 
    // or use context. 
    // For now, let's just rely on a timeout fallback or the fact that App.tsx will unmount/remount 
    // or just cover this component. 
    // Actually, SpecialtySelector is ALWAYS mounted in App.tsx.
    // We can pass a strict "onReset" or monitor a prop.
    // But simpler: just auto-hide the "Preparing" loader after a safe delay if no event received,
    // or rely on the `showSessionSummary` prop passed to us? 
    // Wait, `SessionSummaryPopup` is rendered *inside* `SpecialtySelector` in the original code?
    // Yes, line 2394: `{showSessionSummary && ... <SessionSummaryPopup ... />}`
    // So we DO know when it opens.
  }, [showSessionSummary]);

  // When summary opens, stop preparing
  useEffect(() => {
    if (showSessionSummary) {
      setIsPreparingResults(false);
      setShowSelector(true); // Ready for next time
    }
  }, [showSessionSummary]);

  const handleCloseSessionSummary = () => {
    setShowSessionSummary(false);
    setCompletedSpecialty(null);
    setCompletedTopic(null);
    setIsPreparingResults(false);
    setShowSelector(true);
  };

  const handleSpecialtySelect = (specialty: Specialty) => {
    if (specialty.id === 'random') {
      // Get all specialties except the random one
      const availableSpecialties = SPECIALTIES.filter(s => s.id !== 'random');
      // Pick a random specialty
      const randomSpecialty = availableSpecialties[Math.floor(Math.random() * availableSpecialties.length)];
      // Pick a random topic from that specialty
      const randomTopic = randomSpecialty.topics[Math.floor(Math.random() * randomSpecialty.topics.length)];

      // Directly handle the topic selection without showing intermediate screens
      handleTopicSelect(randomSpecialty, randomTopic);
    } else {
      setSelectedSpecialty(specialty);
    }
  };

  const handleUpgrade = () => {
    try {
      const paystackKey = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY;
      if (!paystackKey) {
        throw new Error('Paystack public key not found');
      }

      // Get phone from user metadata
      const userPhone = user?.user_metadata?.phone || '';

      const handler = PaystackPop.setup({
        key: paystackKey,
        email: user?.email || '',
        amount: 20000 * 100,
        currency: 'NGN',
        phone: userPhone,
        ref: `sub_${Date.now()}_${Math.floor(Math.random() * 1000000)}`,
        metadata: {
          userId: user?.id,
          phone: userPhone,
          custom_fields: [
            {
              display_name: "Subscription Type",
              variable_name: "subscription_type",
              value: "premium"
            },
            {
              display_name: "Phone Number",
              variable_name: "phone_number",
              value: userPhone
            }
          ]
        },
        callback: async (response: any) => {
          try {
            if (!user?.email) {
              alert("User email is missing");
              return;
            }

            await subscriptionService.updateSubscription(user.email, 'premium');
            window.location.reload();
          } catch (error) {
            console.error('Error updating subscription:', error);
            alert('Failed to update subscription status. Please contact support.');
          }
        },
        onClose: () => {
          console.log('Payment window closed');
        }
      });

      handler.openIframe();
    } catch (error) {
      console.error('Paystack initialization error:', error);
      alert('Failed to initialize payment. Please try again later.');
    }
  };

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    markOnboardingComplete();
  };


  return (
    <div className="specialty-selector">
      {showOnboarding && (
        <div className="onboarding-overlay">
          <div className="onboarding-content">
            <h2>Welcome to SimplePLAB<sup>2</sup> 👋</h2>
            <p>Let's get you started with your first case!</p>
            <ol>
              <li>Choose a specialty from the list</li>
              <li>Select a specific case to practice</li>
              <li>Complete the case within the time limit</li>
            </ol>
            <button onClick={handleOnboardingComplete}>Got it!</button>
          </div>
        </div>
      )}

      {showRegistrationPrompt && (
        <div className="registration-prompt">
          <div className="prompt-content">
            <h3>⚠️ Sign in or Sign up to access more cases</h3>
            <div className="prompt-buttons">
              <button onClick={() => navigate('/login')}>Sign In</button>
              <button onClick={() => navigate('/register')}>Sign Up</button>
            </div>
          </div>
        </div>
      )}

      <div className="specialty-grid">
        <div className="specialty-selector-container">
          <header className="selector-header">
            <div className="header-title">
              <Stethoscope className="icon" />
              <h2>
                {selectedTopic
                  ? "Current Scenario"
                  : (selectedSpecialty ? "Select Topic" : "Select a Specialty")}
              </h2>
            </div>
          </header>

          <div className="content-container">
            {selectedTopic && (
              <SessionTimer
                onTimeUp={handleTimeUp}
                onGuideUpdate={setGuideMessage}
                specialtyId={selectedSpecialty?.id || 'default'}
                onShowLoadingSummary={() => setShowSessionSummary(true)}
              />
            )}
            {selectedTopic ? (
              <div className="selected-topic">
                <div className="topic-item">
                  <h4>
                    {selectedSpecialty?.icon}
                    {selectedTopic.name}
                  </h4>
                  <p>{selectedTopic.description}</p>
                </div>

                {guideMessage && (
                  <div className={`guide-message ${guideMessage.includes('Exit') ? 'alert' : ''}`}>
                    {guideMessage}
                  </div>
                )}
              </div>
            ) : showSelector && (
              <>
                {isExpanded && (
                  <div className="selector-content">
                    {selectedSpecialty ? (
                      <div className="content-container">
                        <div className="navigation-header">
                          <button className="back-button" onClick={handleBack}>
                            <FaChevronLeft /> Back
                          </button>
                          <div className="breadcrumb">
                            <span className="specialty-name">
                              {selectedSpecialty.icon}
                              {selectedSpecialty.name}
                            </span>
                          </div>
                        </div>
                        <div className="topics-list">
                          {selectedSpecialty.topics.map((topic) => {
                            const grade = getTopicGrade(topic.name); // Using name as ID proxy for now
                            return (
                              <div
                                key={topic.id}
                                className={`topic-item ${grade ? (grade.passed ? 'passed' : 'failed') : ''}`}
                                onClick={() => handleTopicSelect(selectedSpecialty, topic)}
                              >
                                <div className="topic-content">
                                  <h4>
                                    {selectedSpecialty.icon}
                                    {topic.name}
                                  </h4>
                                  {grade && (
                                    <div className="topic-grade">
                                      <span className="grade-score">{grade.score}/12</span>
                                      {grade.passed ? <FaCheckCircle className="grade-icon pass" /> : <FaTimesCircle className="grade-icon fail" />}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="specialties-list grid grid-cols-2 sm:grid-cols-3 gap-4">
                        {SPECIALTIES.map((specialty) => {
                          const { percent, completed } = getSpecialtyProgress(specialty);
                          return (
                            <SpecialtyButton
                              key={specialty.id}
                              icon={specialty.icon}
                              label={specialty.name}
                              onClick={() => handleSpecialtySelect(specialty)}
                              className="additional-class"
                              progress={percent}
                              isCompleted={completed}
                            />
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {showUpgradePopup && (
              <UpgradePopup
                onUpgrade={handleUpgrade}
                onClose={() => setShowUpgradePopup(false)}
                isExpired={isExpired}
              />
            )}
            {showSessionSummary && user && completedSpecialty && completedTopic && (
              <SessionSummaryPopup
                specialtyName={completedSpecialty.name}
                topicName={completedTopic.name}
                onClose={handleCloseSessionSummary}
                isOpen={showSessionSummary}
              />
            )}

            {/* Preparing Results Loader */}
            {isPreparingResults && (
              <div className="preparing-results-overlay">
                <div className="loader-content">
                  <div className="spinner"></div>
                  <h3>Preparing Results...</h3>
                  <p>Analyzing your performance</p>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Guide messages by station type ───
const GUIDE_MESSAGES = {
  default: {
    initial: '👉 Read the question in 20 seconds and prepare to start.',
    360: '👉 GRIPS! Say "I am Dr… here to help you. Confirm name & age"',
    300: '👉 History! P3MAFTOSA, SOCRATES, ODIPARA, ICE, FAMISH etc',
    180: '👉 Examine this patient within 45 secs',
    120: '👉 2 mins to go. Diagnose, investigate and manage now',
    60: '👉 1 min to go. Last chance… manage now',
    30: '👉 Wrap up nicely. Closing statements, safety netting, thank you',
    5: '👉 Exit station and await grading',
  },
  teaching: {
    initial: '👉 Read the task. Identify what you are teaching and to whom.',
    360: '👉 Introduce yourself. Clarify the learner\'s baseline knowledge.',
    300: '👉 Use Pendleton\'s: Demo → Explain → Let them ask questions',
    180: '👉 Check understanding. Ask "Can you talk me through that?"',
    120: '👉 2 mins left. Summarise key points and pitfalls to avoid.',
    60: '👉 1 min left. Confirm learner confidence, offer resources.',
    30: '👉 Wrap up. Any final questions? Encourage independent practice.',
    5: '👉 Exit station and await grading',
  },
  procedures: {
    initial: '👉 Read the task. Identify the procedure and clinical context.',
    360: '👉 Introduce yourself, confirm patient identity, explain the procedure.',
    300: '👉 Gain informed consent: risks, benefits, alternatives.',
    180: '👉 Describe step-by-step: equipment, aseptic technique, landmarks.',
    120: '👉 2 mins left. Talk through the key steps verbally now!',
    60: '👉 1 min left. Document, dispose sharps, aftercare instructions.',
    30: '👉 Wrap up. Safety-net the patient and thank them.',
    5: '👉 Exit station and await grading',
  },
  simman: {
    initial: '👉 Read the scenario. Prepare your ABCDE approach.',
    360: '👉 Start with A-B-C-D-E assessment. Call for help early!',
    300: '👉 Request monitoring. Treat abnormalities as you find them.',
    180: '👉 Reassess after interventions. Check the monitor response.',
    120: '👉 2 mins left. Summarise findings to the examiner.',
    60: '👉 1 min left. Escalate and document. Consider senior review.',
    30: '👉 Wrap up. Present your diagnosis and ongoing plan.',
    5: '👉 Exit station and await grading',
  },
};

function SessionTimer({ onTimeUp, onGuideUpdate, specialtyId, onShowLoadingSummary }: { onTimeUp: () => void, onGuideUpdate: (msg: string) => void, specialtyId: string, onShowLoadingSummary: () => void }) {
  const TOTAL_DURATION = 6.5 * 60;
  const [timeLeft, setTimeLeft] = useState(TOTAL_DURATION);
  const { client } = useLiveAPIContext();
  const [isPaused, setIsPaused] = useState(false);
  const warningMessageSentRef = useRef(false);
  const isWarningRef = useRef(false);
  const lastTickRef = useRef<number>(Date.now());
  const messageTimeoutRef = useRef<NodeJS.Timeout>();
  const audioRecorderRef = useRef<HTMLElement | null>(null);
  const exitMessageSentRef = useRef(false);

  // Determine guide set from specialty ID
  const guideKey = (['teaching', 'procedures', 'simman'].includes(specialtyId) ? specialtyId : 'default') as keyof typeof GUIDE_MESSAGES;
  const guides = GUIDE_MESSAGES[guideKey];

  // Handle initial message
  useEffect(() => {
    onGuideUpdate(guides.initial);
    messageTimeoutRef.current = setTimeout(() => {
      onGuideUpdate('');
    }, 5000);

    return () => {
      if (messageTimeoutRef.current) {
        clearTimeout(messageTimeoutRef.current);
      }
    };
  }, []);

  // Handle timer and subsequent messages
  useEffect(() => {
    if (isPaused || timeLeft <= 0) return;

    const tick = () => {
      const now = Date.now();
      const delta = now - lastTickRef.current;

      if (delta >= 1000) {
        lastTickRef.current = now;

        setTimeLeft(prev => {
          const newTime = prev - 1;

          if (messageTimeoutRef.current) {
            clearTimeout(messageTimeoutRef.current);
          }

          if (newTime === 6 * 60) {
            client.send([{
              text: `SAY "Enter the station and introduce yourself".
                    `
            }]);
            client.send([{
              text: `SAY "Enter the station and introduce yourself".
                    `
            }]);
            onGuideUpdate(guides[360]);
            messageTimeoutRef.current = setTimeout(() => onGuideUpdate(''), 5000);
          } else if (newTime === 5 * 60) {
            onGuideUpdate(guides[300]);
            messageTimeoutRef.current = setTimeout(() => onGuideUpdate(''), 5000);
          } else if (newTime === 3 * 60) {
            onGuideUpdate(guides[180]);
            messageTimeoutRef.current = setTimeout(() => onGuideUpdate(''), 5000);
          } else if (newTime === 2 * 60) {
            client.send([{
              text: `SAY "2 MINUTES MORE", then continue your previous convo as the patient.
                    `
            }]);
            client.send([{
              text: `SAY "2 MINUTES MORE", then continue your previous convo as the patient.
                    `
            }]);
            onGuideUpdate(guides[120]);
            messageTimeoutRef.current = setTimeout(() => onGuideUpdate(''), 5000);
          } else if (newTime === 1 * 60) {
            onGuideUpdate(guides[60]);
            messageTimeoutRef.current = setTimeout(() => onGuideUpdate(''), 5000);
            warningMessageSentRef.current = true;
            isWarningRef.current = true;
          } else if (newTime === 30) {
            onGuideUpdate(guides[30]);
            messageTimeoutRef.current = setTimeout(() => onGuideUpdate(''), 5000);
          } else if (newTime === 5) {
            if (!exitMessageSentRef.current) {
              exitMessageSentRef.current = true;
              sendExitMessage();
              onShowLoadingSummary(); // Trigger loading modal
              onGuideUpdate(guides[5]);
              messageTimeoutRef.current = setTimeout(() => onGuideUpdate(''), 5000);
            }
          } else if (newTime <= 0) {
            cleanup();
          }

          return Math.max(0, newTime);
        });
      }

      if (timeLeft > 0) {
        requestAnimationFrame(tick);
      }
    };

    const animationFrame = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(animationFrame);
      if (messageTimeoutRef.current) {
        clearTimeout(messageTimeoutRef.current);
      }
    };
  }, [isPaused, timeLeft]);

  const sendExitMessage = async () => {
    await client?.send([{
      text: `Announce this: "Exit the station and await your performance grading"
      
      Then immediately start Performance Analysis:
      Please analyze the candidate's performance in the following areas (including where they could'nt cover ie loss of marks for not covering all areas):
      1. Interpersonal Skills
         - Communication clarity and empathy
         - Professional demeanor
         - Patient rapport building
      
      2. Data Gathering
         - Relevant history taking
         - Appropriate examination suggestions
         - Systematic approach
      
      3. Clinical Management
         - Accuracy of diagnosis
         - Appropriateness of management plan
         - Safety netting
         - Documentation and referrals
         - Senior involvement if needed
      
      4. Overall Assessment
         - Key strengths
         - Areas for improvement
         - Specific suggestions for development
         - Score over 12 (4marks obtainable in each section) and 9 is the maximum score you can give an excellent candidate. State whether candidate passed or failed.
      
      Provide detailed feedback in each area, take a wholistic view and suggest specific improvements.`
    }]);
  };

  const cleanup = async () => {
    if (audioRecorderRef.current) {
      const isRecording = audioRecorderRef.current.getAttribute('aria-pressed') === 'true';
      if (isRecording) {
        audioRecorderRef.current.click();
      }
    }

    await new Promise(resolve => setTimeout(resolve, 500));
    onTimeUp();
  };

  // Circular timer calculations
  const radius = 32;
  const circumference = 2 * Math.PI * radius;
  const progress = timeLeft / TOTAL_DURATION;
  const dashOffset = circumference * (1 - progress);

  // Color transitions: teal → amber → red
  const getTimerColor = () => {
    if (timeLeft < 60) return '#ef4444'; // red
    if (timeLeft < 2 * 60) return '#f59e0b'; // amber
    return '#14b8a6'; // teal
  };

  const isWarning = timeLeft < 60;
  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;

  return (
    <div className="timer-container">
      <div className={`session-timer circular-timer ${isWarning ? 'warning' : ''}`} style={{ '--timer-color': getTimerColor() } as React.CSSProperties}>
        <svg viewBox="0 0 80 80" className="timer-ring">
          {/* Background track */}
          <circle cx="40" cy="40" r={radius} fill="none" stroke="rgba(0,0,0,0.08)" strokeWidth="4" />
          {/* Progress arc */}
          <circle
            cx="40" cy="40" r={radius}
            fill="none"
            stroke={getTimerColor()}
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            transform="rotate(-90 40 40)"
            className="timer-progress"
          />
        </svg>
        <div className="timer-text">
          <span className="timer-digits">{minutes}:{seconds.toString().padStart(2, '0')}</span>
        </div>
      </div>
    </div>
  )
}

